import { createContext, useContext, useState, useCallback, useEffect } from "react";

// Default translations - English is the base
const DEFAULT_TRANSLATIONS = {
  en: {
    // Navigation & Layout
    "nav.dashboard": "Dashboard",
    "nav.api_keys": "API Keys",
    "nav.analytics": "Analytics",
    "nav.billing": "Billing & Plans",
    "nav.api_docs": "API Documentation",
    "nav.settings": "Settings",
    "nav.help": "Help & Support",
    "nav.admin_portal": "Admin Portal",
    "nav.customer_portal": "Customer Portal",
    "nav.system_overview": "System Overview",
    "nav.device_management": "Device Management",
    "nav.tenant_management": "Tenant Management",
    "nav.audit_logs": "Audit Logs",
    "nav.alerts": "Alerts",
    "nav.widget_builder": "Widget Builder",
    "nav.live_dashboard": "Live Dashboard",
    "nav.role_management": "Role Management",
    "nav.translations": "Translations",
    "nav.report_builder": "Report Builder",
    "nav.reports": "Reports",
    "nav.data_archival": "Data Archival",
    "nav.plan_management": "Plan Management",
    "nav.email_config": "Email Configuration",
    "nav.security": "Security",
    "nav.entropy_pool": "Entropy Pool",
    "nav.pqc": "Post-Quantum Crypto",
    "nav.ip_management": "IP Management",

    // Common Actions
    "action.save": "Save",
    "action.cancel": "Cancel",
    "action.delete": "Delete",
    "action.edit": "Edit",
    "action.create": "Create",
    "action.search": "Search",
    "action.filter": "Filter",
    "action.export": "Export",
    "action.import": "Import",
    "action.upload": "Upload",
    "action.download": "Download",
    "action.submit": "Submit",
    "action.next": "Next",
    "action.previous": "Previous",
    "action.back": "Back",
    "action.close": "Close",
    "action.confirm": "Confirm",
    "action.add": "Add",
    "action.remove": "Remove",
    "action.view": "View",
    "action.copy": "Copy",
    "action.duplicate": "Duplicate",
    "action.refresh": "Refresh",
    "action.run": "Run",
    "action.schedule": "Schedule",
    "action.archive": "Archive",
    "action.restore": "Restore",
    "action.request": "Request",
    "action.login": "Sign In",
    "action.logout": "Sign Out",
    "action.register": "Register",
    "action.revoke": "Revoke",
    "action.rotate": "Rotate",
    "action.generate": "Generate",
    "action.verify": "Verify",
    "action.sign": "Sign",
    "action.block": "Block",
    "action.unblock": "Unblock",
    "action.resolve": "Resolve",
    "action.enable": "Enable",
    "action.disable": "Disable",

    // Common Labels
    "label.name": "Name",
    "label.email": "Email",
    "label.password": "Password",
    "label.description": "Description",
    "label.status": "Status",
    "label.type": "Type",
    "label.date": "Date",
    "label.time": "Time",
    "label.created": "Created",
    "label.updated": "Updated",
    "label.actions": "Actions",
    "label.all": "All",
    "label.none": "None",
    "label.yes": "Yes",
    "label.no": "No",
    "label.active": "Active",
    "label.inactive": "Inactive",
    "label.enabled": "Enabled",
    "label.disabled": "Disabled",
    "label.pending": "Pending",
    "label.language": "Language",
    "label.select_language": "Select Language",
    "label.date_range": "Date Range",
    "label.from": "From",
    "label.to": "To",
    "label.total": "Total",
    "label.size": "Size",
    "label.format": "Format",
    "label.id": "ID",
    "label.version": "Version",
    "label.algorithm": "Algorithm",
    "label.key": "Key",
    "label.value": "Value",
    "label.ip_address": "IP Address",
    "label.reason": "Reason",
    "label.role": "Role",
    "label.user": "User",
    "label.tenant": "Tenant",
    "label.organization": "Organization",
    "label.plan": "Plan",
    "label.usage": "Usage",
    "label.quota": "Quota",
    "label.last_seen": "Last Seen",
    "label.created_at": "Created At",
    "label.expires_at": "Expires At",
    "label.never": "Never",
    "label.unlimited": "Unlimited",
    "label.online": "Online",
    "label.offline": "Offline",
    "label.unknown": "Unknown",
    "label.loading": "Loading...",
    "label.no_data": "No data available",
    "label.optional": "optional",
    "label.required": "Required",
    "label.prefix": "Prefix",
    "label.last_used": "Last Used",

    // Login Page
    "login.title": "Sign in to your account",
    "login.subtitle": "Welcome back! Please enter your details.",
    "login.email_placeholder": "Enter your email",
    "login.password_placeholder": "Enter your password",
    "login.remember_me": "Remember me",
    "login.forgot_password": "Forgot password?",
    "login.no_account": "Don't have an account?",
    "login.sign_up": "Sign up",
    "login.or_continue": "Or continue with",
    "login.account_locked": "Account Locked",
    "login.too_many_attempts": "Too many failed login attempts. Please try again in",
    "login.seconds": "seconds",

    // Register Page
    "register.title": "Create your account",
    "register.subtitle": "Start your quantum-safe journey today",
    "register.first_name": "First Name",
    "register.last_name": "Last Name",
    "register.confirm_password": "Confirm Password",
    "register.create_account": "Create Account",
    "register.already_have_account": "Already have an account?",
    "register.sign_in": "Sign in",
    "register.passwords_must_match": "Passwords must match",
    "register.accept_terms": "I accept the Terms of Service and Privacy Policy",

    // 2FA
    "2fa.title": "Two-Factor Authentication",
    "2fa.subtitle": "Enter the verification code to continue",
    "2fa.enter_code": "Enter 6-digit code",
    "2fa.resend": "Resend code",
    "2fa.code_sent": "Code sent to your email",
    "2fa.setup_title": "Set Up Two-Factor Authentication",
    "2fa.scan_qr": "Scan this QR code with your authenticator app",
    "2fa.backup_codes": "Backup Codes",
    "2fa.download_codes": "Download Backup Codes",

    // Tenant Onboarding
    "tenant.org_name": "Organization Name",
    "tenant.org_type": "Organization Type",
    "tenant.industry": "Industry",
    "tenant.company_size": "Company Size",
    "tenant.country": "Country",
    "tenant.timezone": "Timezone",
    "tenant.primary_contact": "Primary Contact",
    "tenant.billing_email": "Billing Email",

    // Industry Options
    "industry.technology": "Technology",
    "industry.finance": "Finance & Banking",
    "industry.healthcare": "Healthcare",
    "industry.government": "Government",
    "industry.defense": "Defense & Aerospace",
    "industry.telecommunications": "Telecommunications",
    "industry.energy": "Energy & Utilities",
    "industry.manufacturing": "Manufacturing",
    "industry.retail": "Retail & E-commerce",
    "industry.education": "Education",
    "industry.media": "Media & Entertainment",
    "industry.gaming": "Gaming & Gambling",
    "industry.automotive": "Automotive",
    "industry.research": "Research & Academia",
    "industry.other": "Other",

    // Organization Type Options
    "org_type.enterprise": "Enterprise",
    "org_type.startup": "Startup",
    "org_type.smb": "Small/Medium Business",
    "org_type.government": "Government Agency",
    "org_type.nonprofit": "Non-Profit",
    "org_type.educational": "Educational Institution",

    // Company Size Options
    "company_size.1-10": "1-10 employees",
    "company_size.11-50": "11-50 employees",
    "company_size.51-200": "51-200 employees",
    "company_size.201-500": "201-500 employees",
    "company_size.501-1000": "501-1000 employees",
    "company_size.1001-5000": "1001-5000 employees",
    "company_size.5000+": "5000+ employees",

    // Device Management
    "device.title": "Device Management",
    "device.subtitle": "Register and monitor QRNG hardware devices",
    "device.register": "Register Device",
    "device.search": "Search devices...",
    "device.device_id": "Device ID",
    "device.model": "Model",
    "device.firmware": "Firmware",
    "device.health": "Health",
    "device.speed": "Speed",
    "device.tamper": "Tamper",
    "device.qcnr": "QCNR",
    "device.no_devices": "No devices registered",
    "device.connect_first": "Register your first QRNG device to start ingesting quantum entropy",
    "device.delete_confirm": "Delete this device?",
    "device.registered_at": "Registered",
    "device.last_heartbeat": "Last Heartbeat",
    "device.connect_info": "Connect via WebSocket to",
    "device.certificate_info": "mTLS certificate required",

    // Audit Logs
    "audit.title": "Audit Logs",
    "audit.subtitle": "Review all administrative and API actions",
    "audit.search": "Search logs...",
    "audit.event_type": "Event Type",
    "audit.all_events": "All Events",
    "audit.success": "Success",
    "audit.failed": "Failed",
    "audit.export_csv": "Export CSV",
    "audit.no_logs": "No audit logs found",
    "audit.user_agent": "User Agent",
    "audit.endpoint": "Endpoint",
    "audit.method": "Method",
    "audit.response_code": "Response Code",
    "audit.ip": "IP Address",
    "audit.details": "Details",

    // System Overview
    "system.title": "System Overview",
    "system.subtitle": "Real-time platform health and performance metrics",
    "system.total_tenants": "Total Tenants",
    "system.active_api_keys": "Active API Keys",
    "system.entropy_events": "Entropy Events",
    "system.system_health": "System Health",
    "system.cpu_usage": "CPU Usage",
    "system.memory_usage": "Memory Usage",
    "system.uptime": "Uptime",
    "system.redis_status": "Redis Status",
    "system.db_status": "Database Status",
    "system.api_requests_today": "API Requests Today",
    "system.alerts_active": "Active Alerts",
    "system.connected_devices": "Connected Devices",

    // Alerts
    "alert.title": "Alerts",
    "alert.subtitle": "Monitor system alerts and notifications",
    "alert.no_alerts": "No active alerts",
    "alert.severity": "Severity",
    "alert.all_severity": "All Severity",
    "alert.critical": "Critical",
    "alert.warning": "Warning",
    "alert.info": "Info",
    "alert.resolved": "Resolved",
    "alert.unresolved": "Unresolved",
    "alert.mark_resolved": "Mark as Resolved",
    "alert.timestamp": "Timestamp",
    "alert.message": "Message",
    "alert.source": "Source",

    // IP Management
    "ip.title": "IP Management",
    "ip.subtitle": "Block and manage IP addresses for platform security",
    "ip.block_ip": "Block IP Address",
    "ip.blocked_ips": "Blocked IPs",
    "ip.ip_address": "IP Address",
    "ip.blocked_at": "Blocked At",
    "ip.no_blocked_ips": "No IPs currently blocked",
    "ip.unblock_confirm": "Unblock this IP address?",
    "ip.block_reason": "Reason for blocking",
    "ip.permanent": "Permanent",
    "ip.temporary": "Temporary",
    "ip.expires": "Expires",

    // Tenant Management (Admin)
    "tenant_mgmt.title": "Tenant Management",
    "tenant_mgmt.subtitle": "Manage all platform tenants and their configurations",
    "tenant_mgmt.create": "Onboard Tenant",
    "tenant_mgmt.search": "Search tenants...",
    "tenant_mgmt.owner": "Owner",
    "tenant_mgmt.api_usage": "API Usage",
    "tenant_mgmt.no_tenants": "No tenants found",
    "tenant_mgmt.delete_confirm": "Delete this tenant? This action cannot be undone.",
    "tenant_mgmt.view_details": "View Details",
    "tenant_mgmt.drbg_config": "DRBG Configuration",
    "tenant_mgmt.dedicated_pool": "Dedicated Pool",

    // Customer Dashboard
    "customer_dash.title": "Dashboard",
    "customer_dash.welcome": "Welcome back",
    "customer_dash.api_calls_today": "API Calls Today",
    "customer_dash.quota_used": "Quota Used",
    "customer_dash.quota_remaining": "Remaining",
    "customer_dash.recent_requests": "Recent Requests",
    "customer_dash.quick_start": "Quick Start",
    "customer_dash.view_docs": "View Documentation",
    "customer_dash.get_api_key": "Get API Key",
    "customer_dash.entropy_source": "Entropy Source",
    "customer_dash.plan_limit": "Plan Limit",
    "customer_dash.this_month": "This Month",
    "customer_dash.uptime": "Uptime",

    // API Keys (Customer)
    "api_keys.title": "API Keys",
    "api_keys.subtitle": "Manage your API keys for platform access",
    "api_keys.create": "Create API Key",
    "api_keys.no_keys": "No API keys yet",
    "api_keys.create_first": "Create your first API key to start using the QRNG API",
    "api_keys.key_name": "Key Name",
    "api_keys.key_prefix": "Prefix",
    "api_keys.copy_key": "Copy full key",
    "api_keys.key_created": "API key created",
    "api_keys.copy_now": "Copy this key now — it won't be shown again",
    "api_keys.revoke_confirm": "Revoke this API key? This cannot be undone.",
    "api_keys.never_used": "Never used",
    "api_keys.permissions": "Permissions",
    "api_keys.read_only": "Read Only",
    "api_keys.full_access": "Full Access",

    // Analytics (Customer)
    "analytics.title": "Analytics",
    "analytics.subtitle": "API usage statistics and trends",
    "analytics.requests_over_time": "Requests Over Time",
    "analytics.top_endpoints": "Top Endpoints",
    "analytics.response_times": "Response Times",
    "analytics.error_rate": "Error Rate",
    "analytics.total_requests": "Total Requests",
    "analytics.successful": "Successful",
    "analytics.failed": "Failed",
    "analytics.avg_latency": "Avg Latency",
    "analytics.p99_latency": "P99 Latency",
    "analytics.period_day": "Today",
    "analytics.period_week": "This Week",
    "analytics.period_month": "This Month",
    "analytics.bytes_generated": "Bytes Generated",
    "analytics.no_data": "No analytics data for this period",

    // Settings (Customer)
    "settings.title": "Settings",
    "settings.profile": "Profile",
    "settings.security": "Security",
    "settings.notifications": "Notifications",
    "settings.api_settings": "API Settings",
    "settings.change_password": "Change Password",
    "settings.current_password": "Current Password",
    "settings.new_password": "New Password",
    "settings.confirm_new_password": "Confirm New Password",
    "settings.save_profile": "Save Profile",
    "settings.first_name": "First Name",
    "settings.last_name": "Last Name",
    "settings.phone": "Phone",
    "settings.company": "Company",
    "settings.timezone": "Timezone",
    "settings.email_notifications": "Email Notifications",
    "settings.usage_alerts": "Usage Alerts",
    "settings.security_alerts": "Security Alerts",
    "settings.marketing_emails": "Marketing Emails",
    "settings.two_factor": "Two-Factor Authentication",
    "settings.2fa_enabled": "2FA is enabled",
    "settings.2fa_disabled": "2FA is disabled",
    "settings.enable_2fa": "Enable 2FA",
    "settings.disable_2fa": "Disable 2FA",
    "settings.profile_updated": "Profile updated successfully",
    "settings.password_changed": "Password changed successfully",

    // PQC
    "pqc.title": "Post-Quantum Cryptography",
    "pqc.subtitle": "NIST-standardized algorithms: ML-KEM-768, ML-DSA-65, SLH-DSA-SHAKE-256s",
    "pqc.kem_title": "ML-KEM-768 (FIPS 203)",
    "pqc.kem_subtitle": "Key Encapsulation — replaces RSA/ECDH key exchange",
    "pqc.dsa_title": "ML-DSA-65 (FIPS 204)",
    "pqc.dsa_subtitle": "Digital Signature — replaces ECDSA/RSA signatures",
    "pqc.slhdsa_title": "SLH-DSA-SHAKE-256s (FIPS 205)",
    "pqc.slhdsa_subtitle": "Hash-Based Signature — NIST Level 5, no lattice assumptions",
    "pqc.vault_title": "Secure Key Vault",
    "pqc.vault_subtitle": "Save and manage generated key pairs",
    "pqc.generate_keypair": "Generate Key Pair",
    "pqc.save_to_vault": "Save to Vault",
    "pqc.key_name_placeholder": "Key name (optional)",
    "pqc.no_keys_in_vault": "No keys saved yet",
    "pqc.delete_key_confirm": "Delete this key? This cannot be undone.",
    "pqc.key_saved": "Key pair saved to vault",
    "pqc.key_deleted": "Key deleted",
    "pqc.encapsulate": "Encapsulate",
    "pqc.decapsulate": "Decapsulate",
    "pqc.match_success": "Shared secrets match — key exchange successful!",
    "pqc.match_fail": "Mismatch — key exchange failed",
    "pqc.sig_valid": "Signature valid — message authenticated!",
    "pqc.sig_invalid": "Signature INVALID",

    // VRF
    "vrf.title": "Verifiable Random Function",
    "vrf.subtitle": "Elliptic Curve VRF (RFC 9381 / ECVRF-P256-SHA256-TAI)",
    "vrf.generate": "Generate VRF",
    "vrf.verify": "Verify Proof",
    "vrf.public_key": "Public Key",
    "vrf.rotate_key": "Rotate Key",
    "vrf.input_data": "Input Data",
    "vrf.output": "VRF Output",
    "vrf.proof": "Proof",
    "vrf.valid": "Valid",
    "vrf.invalid": "Invalid",
    "vrf.key_version": "Key Version",
    "vrf.rotate_confirm": "Rotate ECVRF key? Old proofs will no longer verify.",
    "vrf.key_rotated": "ECVRF key rotated successfully",

    // Entropy Pool
    "entropy.title": "Entropy Pool Dashboard",
    "entropy.subtitle": "Real-time Redis entropy pool — live QRNG hardware device data",
    "entropy.connected_devices": "Connected Devices",
    "entropy.push_events": "Push Events",
    "entropy.fill_level": "Fill Level",
    "entropy.global_pool": "Global Pool",
    "entropy.no_devices": "No devices currently connected",
    "entropy.push_history": "Recent Push Activity",
    "entropy.no_pushes": "No push events yet",
    "entropy.redis_connected": "Redis connected — live QRNG data",
    "entropy.redis_unavailable": "Redis unavailable",
    "entropy.auto_refresh": "Auto-refresh (5s)",
    "entropy.pool_empty": "Pool is currently empty — no pushes recorded",

    // Widget Builder
    "widget.title": "Widget Builder",
    "widget.subtitle": "Create and manage dashboard widgets with custom SQL queries",
    "widget.create": "Create Widget",
    "widget.search": "Search widgets...",
    "widget.type_filter": "Filter by type",
    "widget.all_types": "All Types",
    "widget.kpi_card": "KPI Card",
    "widget.line_chart": "Line Chart",
    "widget.bar_chart": "Bar Chart",
    "widget.pie_chart": "Pie Chart",
    "widget.gauge": "Gauge",
    "widget.data_table": "Data Table",
    "widget.refresh_interval": "Refresh Interval",
    "widget.chart_type": "Chart Type",
    "widget.sql_editor": "SQL Editor",
    "widget.templates": "Templates",
    "widget.schema": "Schema",
    "widget.preview": "Preview",
    "widget.publish": "Publish Widget",
    "widget.save_draft": "Save as Draft",
    "widget.alert_threshold": "Alert Threshold",
    "widget.alert_threshold_hint": "Show badge when value ≥ this",

    // Role Management
    "role.title": "Role Management",
    "role.subtitle": "Define roles and manage permissions for platform access control",
    "role.create": "Create Custom Role",
    "role.search": "Search roles...",
    "role.system_roles": "System Roles",
    "role.custom_roles": "Custom Roles",
    "role.permissions": "Permissions",
    "role.members": "members",
    "role.copy_from": "Copy Permissions From",
    "role.start_scratch": "Start from scratch",

    // Report Builder
    "report.title": "Report Builder",
    "report.subtitle": "Create and manage reports with charts, tables, and drill-down capabilities",
    "report.create": "Create Report",
    "report.search": "Search reports...",
    "report.chart_report": "Chart Report",
    "report.tabular_report": "Tabular Report",
    "report.drilldown_report": "Drill-Down Report",
    "report.schedule_report": "Schedule Report",
    "report.run_now": "Run Now",
    "report.last_run": "Last Run",
    "report.next_run": "Next Run",
    "report.output_format": "Output Format",
    "report.parameters": "Parameters",
    "report.recipients": "Recipients",
    "report.frequency": "Frequency",
    "report.daily": "Daily",
    "report.weekly": "Weekly",
    "report.monthly": "Monthly",
    "report.custom": "Custom",

    // Data Archival
    "archival.title": "Data Archival Management",
    "archival.subtitle": "Manage data retention policies, archive jobs, and retrieval requests",
    "archival.active_data": "Active Data",
    "archival.archived_data": "Archived Data",
    "archival.retention_policy": "Retention Policy",
    "archival.retention_years": "years retention",
    "archival.archive_jobs": "Archive Jobs",
    "archival.retrieval_requests": "Retrieval Requests",
    "archival.run_archive": "Run Archive Job",
    "archival.request_data": "Request Archived Data",
    "archival.job_status": "Job Status",
    "archival.completed": "Completed",
    "archival.in_progress": "In Progress",
    "archival.failed": "Failed",
    "archival.scheduled": "Scheduled",
    "archival.records_archived": "Records Archived",
    "archival.storage_saved": "Storage Saved",
    "archival.data_type": "Data Type",
    "archival.date_range": "Date Range",
    "archival.download_link": "Download Link",
    "archival.expires_in": "Expires in",
    "archival.transactional": "Transactional Data",
    "archival.master": "Master Data",

    // Billing
    "billing.title": "Billing & Plans",
    "billing.subtitle": "Manage your subscription, payment methods, and view invoices",
    "billing.current_plan": "Current Plan",
    "billing.upgrade": "Upgrade Plan",
    "billing.downgrade": "Downgrade",
    "billing.payment_method": "Payment Method",
    "billing.add_payment": "Add Payment Method",
    "billing.invoices": "Invoices",
    "billing.usage": "Usage",
    "billing.api_calls": "API Calls",
    "billing.bandwidth": "Bandwidth",
    "billing.overage": "Overage Charges",
    "billing.no_invoices": "No invoices yet",
    "billing.manage_subscription": "Manage Subscription",

    // Status Messages
    "status.all_systems_operational": "All Systems Operational",
    "status.loading": "Loading...",
    "status.error": "An error occurred",
    "status.success": "Success",
    "status.saving": "Saving...",
    "status.saved": "Saved",
    "status.deleting": "Deleting...",
    "status.deleted": "Deleted",
    "status.copied": "Copied!",
    "status.not_found": "Not Found",

    // Translation Management
    "translation.title": "Translation Management",
    "translation.subtitle": "Manage translations for all supported languages",
    "translation.bulk_upload": "Bulk Upload",
    "translation.export_all": "Export All",
    "translation.add_key": "Add Translation Key",
    "translation.key": "Translation Key",
    "translation.base_value": "Base Value (English)",
    "translation.translation": "Translation",
    "translation.missing": "Missing",
    "translation.translated": "Translated",
    "translation.upload_json": "Upload JSON",
    "translation.upload_csv": "Upload CSV",
    "translation.download_template": "Download Template",

    // Validation Messages
    "validation.email_required": "Email is required",
    "validation.password_required": "Password is required",
    "validation.email_invalid": "Please enter a valid email address",
    "validation.field_required": "This field is required",
    "validation.password_min": "Password must be at least 8 characters",
    "validation.password_uppercase": "Password must contain an uppercase letter",
    "validation.password_number": "Password must contain a number",
    "validation.password_special": "Password must contain a special character",
    "validation.passwords_match": "Passwords must match",
    "validation.name_required": "Name is required",
    "validation.invalid_ip": "Please enter a valid IP address",

    // Security / IP blocking
    "security.title": "Security",
    "security.subtitle": "Manage IP blocklist, rate limits, and access controls",
    "security.blocked_ips": "Blocked IPs",
    "security.block_new_ip": "Block IP",
    "security.no_blocked": "No IPs are currently blocked",
    "security.rate_limiting": "Rate Limiting",
    "security.requests_per_minute": "Requests per minute",

    // Help
    "help.title": "Help & Support",
    "help.subtitle": "Documentation, tutorials, and support resources",
    "help.search": "Search help articles...",
    "help.contact_support": "Contact Support",
    "help.open_ticket": "Open Ticket",
    "help.faq": "FAQ",
    "help.documentation": "Documentation",
    "help.community": "Community",
  },

  fr: {
    "nav.dashboard": "Tableau de bord",
    "nav.api_keys": "Clés API",
    "nav.analytics": "Analytique",
    "nav.billing": "Facturation et forfaits",
    "nav.api_docs": "Documentation API",
    "nav.settings": "Paramètres",
    "nav.help": "Aide et support",
    "nav.admin_portal": "Portail Admin",
    "nav.customer_portal": "Portail Client",
    "nav.system_overview": "Aperçu du système",
    "nav.device_management": "Gestion des appareils",
    "nav.tenant_management": "Gestion des locataires",
    "nav.audit_logs": "Journaux d'audit",
    "nav.alerts": "Alertes",
    "nav.widget_builder": "Constructeur de widgets",
    "nav.live_dashboard": "Tableau de bord en direct",
    "nav.role_management": "Gestion des rôles",
    "nav.translations": "Traductions",
    "nav.report_builder": "Générateur de rapports",
    "nav.reports": "Rapports",
    "nav.data_archival": "Archivage des données",
    "nav.plan_management": "Gestion des forfaits",
    "nav.email_config": "Configuration email",
    "nav.security": "Sécurité",
    "nav.entropy_pool": "Pool d'entropie",
    "nav.pqc": "Crypto post-quantique",
    "nav.ip_management": "Gestion des IP",

    "action.save": "Enregistrer",
    "action.cancel": "Annuler",
    "action.delete": "Supprimer",
    "action.edit": "Modifier",
    "action.create": "Créer",
    "action.search": "Rechercher",
    "action.filter": "Filtrer",
    "action.export": "Exporter",
    "action.import": "Importer",
    "action.upload": "Télécharger",
    "action.download": "Télécharger",
    "action.submit": "Soumettre",
    "action.next": "Suivant",
    "action.previous": "Précédent",
    "action.back": "Retour",
    "action.close": "Fermer",
    "action.confirm": "Confirmer",
    "action.add": "Ajouter",
    "action.remove": "Retirer",
    "action.view": "Voir",
    "action.copy": "Copier",
    "action.duplicate": "Dupliquer",
    "action.refresh": "Actualiser",
    "action.run": "Exécuter",
    "action.schedule": "Planifier",
    "action.archive": "Archiver",
    "action.restore": "Restaurer",
    "action.request": "Demander",
    "action.login": "Se connecter",
    "action.logout": "Se déconnecter",
    "action.register": "S'inscrire",
    "action.revoke": "Révoquer",
    "action.rotate": "Faire pivoter",
    "action.generate": "Générer",
    "action.verify": "Vérifier",
    "action.sign": "Signer",
    "action.block": "Bloquer",
    "action.unblock": "Débloquer",
    "action.resolve": "Résoudre",
    "action.enable": "Activer",
    "action.disable": "Désactiver",

    "label.name": "Nom",
    "label.email": "Email",
    "label.password": "Mot de passe",
    "label.description": "Description",
    "label.status": "Statut",
    "label.type": "Type",
    "label.date": "Date",
    "label.time": "Heure",
    "label.created": "Créé",
    "label.updated": "Mis à jour",
    "label.actions": "Actions",
    "label.all": "Tous",
    "label.none": "Aucun",
    "label.yes": "Oui",
    "label.no": "Non",
    "label.active": "Actif",
    "label.inactive": "Inactif",
    "label.enabled": "Activé",
    "label.disabled": "Désactivé",
    "label.pending": "En attente",
    "label.language": "Langue",
    "label.select_language": "Sélectionner la langue",
    "label.date_range": "Plage de dates",
    "label.from": "De",
    "label.to": "À",
    "label.total": "Total",
    "label.size": "Taille",
    "label.format": "Format",
    "label.id": "ID",
    "label.version": "Version",
    "label.algorithm": "Algorithme",
    "label.key": "Clé",
    "label.value": "Valeur",
    "label.ip_address": "Adresse IP",
    "label.reason": "Raison",
    "label.role": "Rôle",
    "label.user": "Utilisateur",
    "label.tenant": "Locataire",
    "label.organization": "Organisation",
    "label.plan": "Forfait",
    "label.usage": "Utilisation",
    "label.quota": "Quota",
    "label.last_seen": "Dernière activité",
    "label.created_at": "Créé le",
    "label.expires_at": "Expire le",
    "label.never": "Jamais",
    "label.unlimited": "Illimité",
    "label.online": "En ligne",
    "label.offline": "Hors ligne",
    "label.unknown": "Inconnu",
    "label.loading": "Chargement...",
    "label.no_data": "Aucune donnée disponible",
    "label.optional": "optionnel",
    "label.required": "Obligatoire",
    "label.prefix": "Préfixe",
    "label.last_used": "Dernière utilisation",

    "login.title": "Connectez-vous à votre compte",
    "login.subtitle": "Bienvenue ! Veuillez entrer vos informations.",
    "login.email_placeholder": "Entrez votre email",
    "login.password_placeholder": "Entrez votre mot de passe",
    "login.remember_me": "Se souvenir de moi",
    "login.forgot_password": "Mot de passe oublié ?",
    "login.no_account": "Vous n'avez pas de compte ?",
    "login.sign_up": "S'inscrire",
    "login.or_continue": "Ou continuer avec",
    "login.account_locked": "Compte verrouillé",
    "login.too_many_attempts": "Trop de tentatives de connexion échouées. Réessayez dans",
    "login.seconds": "secondes",

    "register.title": "Créez votre compte",
    "register.subtitle": "Commencez votre parcours quantique aujourd'hui",
    "register.first_name": "Prénom",
    "register.last_name": "Nom de famille",
    "register.confirm_password": "Confirmer le mot de passe",
    "register.create_account": "Créer un compte",
    "register.already_have_account": "Vous avez déjà un compte ?",
    "register.sign_in": "Se connecter",
    "register.passwords_must_match": "Les mots de passe doivent correspondre",
    "register.accept_terms": "J'accepte les conditions d'utilisation et la politique de confidentialité",

    "2fa.title": "Authentification à deux facteurs",
    "2fa.subtitle": "Entrez le code de vérification pour continuer",
    "2fa.enter_code": "Entrez le code à 6 chiffres",
    "2fa.resend": "Renvoyer le code",
    "2fa.code_sent": "Code envoyé à votre email",
    "2fa.setup_title": "Configurer l'authentification à deux facteurs",
    "2fa.scan_qr": "Scannez ce QR code avec votre application d'authentification",
    "2fa.backup_codes": "Codes de secours",
    "2fa.download_codes": "Télécharger les codes de secours",

    "tenant.org_name": "Nom de l'organisation",
    "tenant.org_type": "Type d'organisation",
    "tenant.industry": "Secteur d'activité",
    "tenant.company_size": "Taille de l'entreprise",
    "tenant.country": "Pays",
    "tenant.timezone": "Fuseau horaire",
    "tenant.primary_contact": "Contact principal",
    "tenant.billing_email": "Email de facturation",

    "industry.technology": "Technologie",
    "industry.finance": "Finance et Banque",
    "industry.healthcare": "Santé",
    "industry.government": "Gouvernement",
    "industry.defense": "Défense et Aérospatiale",
    "industry.telecommunications": "Télécommunications",
    "industry.energy": "Énergie et Services publics",
    "industry.manufacturing": "Fabrication",
    "industry.retail": "Commerce de détail",
    "industry.education": "Éducation",
    "industry.media": "Médias et Divertissement",
    "industry.gaming": "Jeux et Paris",
    "industry.automotive": "Automobile",
    "industry.research": "Recherche et Académie",
    "industry.other": "Autre",

    "org_type.enterprise": "Grande entreprise",
    "org_type.startup": "Startup",
    "org_type.smb": "PME",
    "org_type.government": "Agence gouvernementale",
    "org_type.nonprofit": "Organisation à but non lucratif",
    "org_type.educational": "Établissement d'enseignement",

    "company_size.1-10": "1-10 employés",
    "company_size.11-50": "11-50 employés",
    "company_size.51-200": "51-200 employés",
    "company_size.201-500": "201-500 employés",
    "company_size.501-1000": "501-1000 employés",
    "company_size.1001-5000": "1001-5000 employés",
    "company_size.5000+": "5000+ employés",

    "device.title": "Gestion des appareils",
    "device.subtitle": "Enregistrer et surveiller les appareils QRNG",
    "device.register": "Enregistrer un appareil",
    "device.search": "Rechercher des appareils...",
    "device.device_id": "ID de l'appareil",
    "device.model": "Modèle",
    "device.firmware": "Micrologiciel",
    "device.health": "Santé",
    "device.speed": "Vitesse",
    "device.tamper": "Falsification",
    "device.qcnr": "QCNR",
    "device.no_devices": "Aucun appareil enregistré",
    "device.connect_first": "Enregistrez votre premier appareil QRNG pour commencer",
    "device.delete_confirm": "Supprimer cet appareil ?",
    "device.registered_at": "Enregistré le",
    "device.last_heartbeat": "Dernière pulsation",
    "device.connect_info": "Connecter via WebSocket à",
    "device.certificate_info": "Certificat mTLS requis",

    "audit.title": "Journaux d'audit",
    "audit.subtitle": "Examinez toutes les actions administratives et API",
    "audit.search": "Rechercher dans les journaux...",
    "audit.event_type": "Type d'événement",
    "audit.all_events": "Tous les événements",
    "audit.success": "Succès",
    "audit.failed": "Échec",
    "audit.export_csv": "Exporter CSV",
    "audit.no_logs": "Aucun journal d'audit trouvé",
    "audit.user_agent": "Agent utilisateur",
    "audit.endpoint": "Point de terminaison",
    "audit.method": "Méthode",
    "audit.response_code": "Code de réponse",
    "audit.ip": "Adresse IP",
    "audit.details": "Détails",

    "system.title": "Aperçu du système",
    "system.subtitle": "Métriques de santé et de performance en temps réel",
    "system.total_tenants": "Total locataires",
    "system.active_api_keys": "Clés API actives",
    "system.entropy_events": "Événements d'entropie",
    "system.system_health": "Santé du système",
    "system.cpu_usage": "Utilisation CPU",
    "system.memory_usage": "Utilisation mémoire",
    "system.uptime": "Disponibilité",
    "system.redis_status": "Statut Redis",
    "system.db_status": "Statut base de données",
    "system.api_requests_today": "Requêtes API aujourd'hui",
    "system.alerts_active": "Alertes actives",
    "system.connected_devices": "Appareils connectés",

    "alert.title": "Alertes",
    "alert.subtitle": "Surveiller les alertes système et notifications",
    "alert.no_alerts": "Aucune alerte active",
    "alert.severity": "Gravité",
    "alert.all_severity": "Toutes les gravités",
    "alert.critical": "Critique",
    "alert.warning": "Avertissement",
    "alert.info": "Info",
    "alert.resolved": "Résolu",
    "alert.unresolved": "Non résolu",
    "alert.mark_resolved": "Marquer comme résolu",
    "alert.timestamp": "Horodatage",
    "alert.message": "Message",
    "alert.source": "Source",

    "ip.title": "Gestion des IP",
    "ip.subtitle": "Bloquer et gérer les adresses IP pour la sécurité",
    "ip.block_ip": "Bloquer une IP",
    "ip.blocked_ips": "IPs bloquées",
    "ip.ip_address": "Adresse IP",
    "ip.blocked_at": "Bloqué le",
    "ip.no_blocked_ips": "Aucune IP bloquée actuellement",
    "ip.unblock_confirm": "Débloquer cette adresse IP ?",
    "ip.block_reason": "Raison du blocage",
    "ip.permanent": "Permanent",
    "ip.temporary": "Temporaire",
    "ip.expires": "Expire",

    "tenant_mgmt.title": "Gestion des locataires",
    "tenant_mgmt.subtitle": "Gérer tous les locataires de la plateforme",
    "tenant_mgmt.create": "Intégrer un locataire",
    "tenant_mgmt.search": "Rechercher des locataires...",
    "tenant_mgmt.owner": "Propriétaire",
    "tenant_mgmt.api_usage": "Utilisation API",
    "tenant_mgmt.no_tenants": "Aucun locataire trouvé",
    "tenant_mgmt.delete_confirm": "Supprimer ce locataire ? Cette action est irréversible.",
    "tenant_mgmt.view_details": "Voir les détails",
    "tenant_mgmt.drbg_config": "Configuration DRBG",
    "tenant_mgmt.dedicated_pool": "Pool dédié",

    "customer_dash.title": "Tableau de bord",
    "customer_dash.welcome": "Bon retour",
    "customer_dash.api_calls_today": "Appels API aujourd'hui",
    "customer_dash.quota_used": "Quota utilisé",
    "customer_dash.quota_remaining": "Restant",
    "customer_dash.recent_requests": "Requêtes récentes",
    "customer_dash.quick_start": "Démarrage rapide",
    "customer_dash.view_docs": "Voir la documentation",
    "customer_dash.get_api_key": "Obtenir une clé API",
    "customer_dash.entropy_source": "Source d'entropie",
    "customer_dash.plan_limit": "Limite du forfait",
    "customer_dash.this_month": "Ce mois-ci",
    "customer_dash.uptime": "Disponibilité",

    "api_keys.title": "Clés API",
    "api_keys.subtitle": "Gérez vos clés API pour accéder à la plateforme",
    "api_keys.create": "Créer une clé API",
    "api_keys.no_keys": "Aucune clé API",
    "api_keys.create_first": "Créez votre première clé API pour utiliser l'API QRNG",
    "api_keys.key_name": "Nom de la clé",
    "api_keys.key_prefix": "Préfixe",
    "api_keys.copy_key": "Copier la clé complète",
    "api_keys.key_created": "Clé API créée",
    "api_keys.copy_now": "Copiez cette clé maintenant — elle ne sera plus affichée",
    "api_keys.revoke_confirm": "Révoquer cette clé API ? Cette action est irréversible.",
    "api_keys.never_used": "Jamais utilisée",
    "api_keys.permissions": "Permissions",
    "api_keys.read_only": "Lecture seule",
    "api_keys.full_access": "Accès complet",

    "analytics.title": "Analytique",
    "analytics.subtitle": "Statistiques d'utilisation de l'API et tendances",
    "analytics.requests_over_time": "Requêtes dans le temps",
    "analytics.top_endpoints": "Principaux endpoints",
    "analytics.response_times": "Temps de réponse",
    "analytics.error_rate": "Taux d'erreur",
    "analytics.total_requests": "Total des requêtes",
    "analytics.successful": "Réussies",
    "analytics.failed": "Échouées",
    "analytics.avg_latency": "Latence moyenne",
    "analytics.p99_latency": "Latence P99",
    "analytics.period_day": "Aujourd'hui",
    "analytics.period_week": "Cette semaine",
    "analytics.period_month": "Ce mois-ci",
    "analytics.bytes_generated": "Octets générés",
    "analytics.no_data": "Pas de données pour cette période",

    "settings.title": "Paramètres",
    "settings.profile": "Profil",
    "settings.security": "Sécurité",
    "settings.notifications": "Notifications",
    "settings.api_settings": "Paramètres API",
    "settings.change_password": "Changer le mot de passe",
    "settings.current_password": "Mot de passe actuel",
    "settings.new_password": "Nouveau mot de passe",
    "settings.confirm_new_password": "Confirmer le nouveau mot de passe",
    "settings.save_profile": "Sauvegarder le profil",
    "settings.first_name": "Prénom",
    "settings.last_name": "Nom de famille",
    "settings.phone": "Téléphone",
    "settings.company": "Entreprise",
    "settings.timezone": "Fuseau horaire",
    "settings.email_notifications": "Notifications par email",
    "settings.usage_alerts": "Alertes d'utilisation",
    "settings.security_alerts": "Alertes de sécurité",
    "settings.marketing_emails": "Emails marketing",
    "settings.two_factor": "Authentification à deux facteurs",
    "settings.2fa_enabled": "2FA activé",
    "settings.2fa_disabled": "2FA désactivé",
    "settings.enable_2fa": "Activer la 2FA",
    "settings.disable_2fa": "Désactiver la 2FA",
    "settings.profile_updated": "Profil mis à jour avec succès",
    "settings.password_changed": "Mot de passe modifié avec succès",

    "pqc.title": "Cryptographie post-quantique",
    "pqc.subtitle": "Algorithmes standardisés NIST : ML-KEM-768, ML-DSA-65, SLH-DSA-SHAKE-256s",
    "pqc.kem_title": "ML-KEM-768 (FIPS 203)",
    "pqc.kem_subtitle": "Encapsulation de clé — remplace RSA/ECDH",
    "pqc.dsa_title": "ML-DSA-65 (FIPS 204)",
    "pqc.dsa_subtitle": "Signature numérique — remplace ECDSA/RSA",
    "pqc.slhdsa_title": "SLH-DSA-SHAKE-256s (FIPS 205)",
    "pqc.slhdsa_subtitle": "Signature basée sur le hachage — Niveau NIST 5",
    "pqc.vault_title": "Coffre de clés sécurisé",
    "pqc.vault_subtitle": "Sauvegarder et gérer les paires de clés générées",
    "pqc.generate_keypair": "Générer une paire de clés",
    "pqc.save_to_vault": "Sauvegarder dans le coffre",
    "pqc.key_name_placeholder": "Nom de la clé (optionnel)",
    "pqc.no_keys_in_vault": "Aucune clé sauvegardée",
    "pqc.delete_key_confirm": "Supprimer cette clé ? Cette action est irréversible.",
    "pqc.key_saved": "Paire de clés sauvegardée",
    "pqc.key_deleted": "Clé supprimée",
    "pqc.encapsulate": "Encapsuler",
    "pqc.decapsulate": "Désencapsuler",
    "pqc.match_success": "Secrets partagés identiques — échange de clés réussi !",
    "pqc.match_fail": "Incompatibilité — échange de clés échoué",
    "pqc.sig_valid": "Signature valide — message authentifié !",
    "pqc.sig_invalid": "Signature INVALIDE",

    "vrf.title": "Fonction aléatoire vérifiable",
    "vrf.subtitle": "ECVRF (RFC 9381 / ECVRF-P256-SHA256-TAI)",
    "vrf.generate": "Générer VRF",
    "vrf.verify": "Vérifier la preuve",
    "vrf.public_key": "Clé publique",
    "vrf.rotate_key": "Faire pivoter la clé",
    "vrf.input_data": "Données d'entrée",
    "vrf.output": "Sortie VRF",
    "vrf.proof": "Preuve",
    "vrf.valid": "Valide",
    "vrf.invalid": "Invalide",
    "vrf.key_version": "Version de la clé",
    "vrf.rotate_confirm": "Faire pivoter la clé ECVRF ? Les anciennes preuves ne seront plus vérifiables.",
    "vrf.key_rotated": "Clé ECVRF pivotée avec succès",

    "entropy.title": "Tableau de bord du pool d'entropie",
    "entropy.subtitle": "Pool d'entropie Redis en temps réel — données de dispositifs QRNG",
    "entropy.connected_devices": "Appareils connectés",
    "entropy.push_events": "Événements push",
    "entropy.fill_level": "Niveau de remplissage",
    "entropy.global_pool": "Pool global",
    "entropy.no_devices": "Aucun appareil connecté actuellement",
    "entropy.push_history": "Activité push récente",
    "entropy.no_pushes": "Aucun événement push encore",
    "entropy.redis_connected": "Redis connecté — données QRNG en direct",
    "entropy.redis_unavailable": "Redis indisponible",
    "entropy.auto_refresh": "Actualisation automatique (5s)",
    "entropy.pool_empty": "Le pool est actuellement vide",

    "widget.title": "Constructeur de widgets",
    "widget.subtitle": "Créez et gérez des widgets de tableau de bord",
    "widget.create": "Créer un widget",
    "widget.search": "Rechercher des widgets...",
    "widget.type_filter": "Filtrer par type",
    "widget.all_types": "Tous les types",
    "widget.kpi_card": "Carte KPI",
    "widget.line_chart": "Graphique en ligne",
    "widget.bar_chart": "Graphique à barres",
    "widget.pie_chart": "Graphique circulaire",
    "widget.gauge": "Jauge",
    "widget.data_table": "Tableau de données",
    "widget.refresh_interval": "Intervalle de rafraîchissement",
    "widget.chart_type": "Type de graphique",
    "widget.sql_editor": "Éditeur SQL",
    "widget.templates": "Modèles",
    "widget.schema": "Schéma",
    "widget.preview": "Aperçu",
    "widget.publish": "Publier le widget",
    "widget.save_draft": "Enregistrer comme brouillon",
    "widget.alert_threshold": "Seuil d'alerte",
    "widget.alert_threshold_hint": "Afficher le badge quand la valeur ≥ ce seuil",

    "role.title": "Gestion des rôles",
    "role.subtitle": "Définissez les rôles et gérez les permissions",
    "role.create": "Créer un rôle personnalisé",
    "role.search": "Rechercher des rôles...",
    "role.system_roles": "Rôles système",
    "role.custom_roles": "Rôles personnalisés",
    "role.permissions": "Permissions",
    "role.members": "membres",
    "role.copy_from": "Copier les permissions depuis",
    "role.start_scratch": "Partir de zéro",

    "report.title": "Générateur de rapports",
    "report.subtitle": "Créez et gérez des rapports avec graphiques et tableaux",
    "report.create": "Créer un rapport",
    "report.search": "Rechercher des rapports...",
    "report.chart_report": "Rapport graphique",
    "report.tabular_report": "Rapport tabulaire",
    "report.drilldown_report": "Rapport d'exploration",
    "report.schedule_report": "Planifier le rapport",
    "report.run_now": "Exécuter maintenant",
    "report.last_run": "Dernière exécution",
    "report.next_run": "Prochaine exécution",
    "report.output_format": "Format de sortie",
    "report.parameters": "Paramètres",
    "report.recipients": "Destinataires",
    "report.frequency": "Fréquence",
    "report.daily": "Quotidien",
    "report.weekly": "Hebdomadaire",
    "report.monthly": "Mensuel",
    "report.custom": "Personnalisé",

    "archival.title": "Gestion de l'archivage des données",
    "archival.subtitle": "Gérez les politiques de rétention et les tâches d'archivage",
    "archival.active_data": "Données actives",
    "archival.archived_data": "Données archivées",
    "archival.retention_policy": "Politique de rétention",
    "archival.retention_years": "ans de rétention",
    "archival.archive_jobs": "Tâches d'archivage",
    "archival.retrieval_requests": "Demandes de récupération",
    "archival.run_archive": "Exécuter l'archivage",
    "archival.request_data": "Demander des données archivées",
    "archival.job_status": "Statut de la tâche",
    "archival.completed": "Terminé",
    "archival.in_progress": "En cours",
    "archival.failed": "Échoué",
    "archival.scheduled": "Planifié",
    "archival.records_archived": "Enregistrements archivés",
    "archival.storage_saved": "Stockage économisé",
    "archival.data_type": "Type de données",
    "archival.date_range": "Plage de dates",
    "archival.download_link": "Lien de téléchargement",
    "archival.expires_in": "Expire dans",
    "archival.transactional": "Données transactionnelles",
    "archival.master": "Données de référence",

    "billing.title": "Facturation et forfaits",
    "billing.subtitle": "Gérez votre abonnement et consultez vos factures",
    "billing.current_plan": "Forfait actuel",
    "billing.upgrade": "Mettre à niveau",
    "billing.downgrade": "Rétrograder",
    "billing.payment_method": "Moyen de paiement",
    "billing.add_payment": "Ajouter un moyen de paiement",
    "billing.invoices": "Factures",
    "billing.usage": "Utilisation",
    "billing.api_calls": "Appels API",
    "billing.bandwidth": "Bande passante",
    "billing.overage": "Frais de dépassement",
    "billing.no_invoices": "Aucune facture encore",
    "billing.manage_subscription": "Gérer l'abonnement",

    "status.all_systems_operational": "Tous les systèmes opérationnels",
    "status.loading": "Chargement...",
    "status.error": "Une erreur s'est produite",
    "status.success": "Succès",
    "status.saving": "Enregistrement...",
    "status.saved": "Enregistré",
    "status.deleting": "Suppression...",
    "status.deleted": "Supprimé",
    "status.copied": "Copié !",
    "status.not_found": "Non trouvé",

    "translation.title": "Gestion des traductions",
    "translation.subtitle": "Gérez les traductions pour toutes les langues prises en charge",
    "translation.bulk_upload": "Téléchargement en masse",
    "translation.export_all": "Tout exporter",
    "translation.add_key": "Ajouter une clé",
    "translation.key": "Clé de traduction",
    "translation.base_value": "Valeur de base (Anglais)",
    "translation.translation": "Traduction",
    "translation.missing": "Manquant",
    "translation.translated": "Traduit",
    "translation.upload_json": "Télécharger JSON",
    "translation.upload_csv": "Télécharger CSV",
    "translation.download_template": "Télécharger le modèle",

    "validation.email_required": "L'adresse email est requise",
    "validation.password_required": "Le mot de passe est requis",
    "validation.email_invalid": "Veuillez saisir une adresse email valide",
    "validation.field_required": "Ce champ est obligatoire",
    "validation.password_min": "Le mot de passe doit contenir au moins 8 caractères",
    "validation.password_uppercase": "Le mot de passe doit contenir une majuscule",
    "validation.password_number": "Le mot de passe doit contenir un chiffre",
    "validation.password_special": "Le mot de passe doit contenir un caractère spécial",
    "validation.passwords_match": "Les mots de passe doivent correspondre",
    "validation.name_required": "Le nom est obligatoire",
    "validation.invalid_ip": "Veuillez saisir une adresse IP valide",

    "security.title": "Sécurité",
    "security.subtitle": "Gérez la liste de blocage IP et les contrôles d'accès",
    "security.blocked_ips": "IPs bloquées",
    "security.block_new_ip": "Bloquer une IP",
    "security.no_blocked": "Aucune IP n'est actuellement bloquée",
    "security.rate_limiting": "Limitation du débit",
    "security.requests_per_minute": "Requêtes par minute",

    "help.title": "Aide et support",
    "help.subtitle": "Documentation, tutoriels et ressources de support",
    "help.search": "Rechercher des articles d'aide...",
    "help.contact_support": "Contacter le support",
    "help.open_ticket": "Ouvrir un ticket",
    "help.faq": "FAQ",
    "help.documentation": "Documentation",
    "help.community": "Communauté",
  },

  es: {
    "nav.dashboard": "Panel de control",
    "nav.api_keys": "Claves API",
    "nav.analytics": "Análisis",
    "nav.billing": "Facturación y planes",
    "nav.api_docs": "Documentación API",
    "nav.settings": "Configuración",
    "nav.help": "Ayuda y soporte",
    "nav.admin_portal": "Portal de administración",
    "nav.customer_portal": "Portal de clientes",
    "nav.system_overview": "Resumen del sistema",
    "nav.device_management": "Gestión de dispositivos",
    "nav.tenant_management": "Gestión de inquilinos",
    "nav.audit_logs": "Registros de auditoría",
    "nav.alerts": "Alertas",
    "nav.widget_builder": "Constructor de widgets",
    "nav.live_dashboard": "Panel en vivo",
    "nav.role_management": "Gestión de roles",
    "nav.translations": "Traducciones",
    "nav.report_builder": "Generador de informes",
    "nav.reports": "Informes",
    "nav.data_archival": "Archivo de datos",
    "nav.plan_management": "Gestión de planes",
    "nav.email_config": "Configuración de email",
    "nav.security": "Seguridad",
    "nav.entropy_pool": "Grupo de entropía",
    "nav.pqc": "Cripto post-cuántica",
    "nav.ip_management": "Gestión de IPs",

    "action.save": "Guardar",
    "action.cancel": "Cancelar",
    "action.delete": "Eliminar",
    "action.edit": "Editar",
    "action.create": "Crear",
    "action.search": "Buscar",
    "action.filter": "Filtrar",
    "action.export": "Exportar",
    "action.import": "Importar",
    "action.upload": "Subir",
    "action.download": "Descargar",
    "action.submit": "Enviar",
    "action.next": "Siguiente",
    "action.previous": "Anterior",
    "action.back": "Volver",
    "action.close": "Cerrar",
    "action.confirm": "Confirmar",
    "action.add": "Añadir",
    "action.remove": "Eliminar",
    "action.view": "Ver",
    "action.copy": "Copiar",
    "action.duplicate": "Duplicar",
    "action.refresh": "Actualizar",
    "action.run": "Ejecutar",
    "action.schedule": "Programar",
    "action.archive": "Archivar",
    "action.restore": "Restaurar",
    "action.request": "Solicitar",
    "action.login": "Iniciar sesión",
    "action.logout": "Cerrar sesión",
    "action.register": "Registrarse",
    "action.revoke": "Revocar",
    "action.rotate": "Rotar",
    "action.generate": "Generar",
    "action.verify": "Verificar",
    "action.sign": "Firmar",
    "action.block": "Bloquear",
    "action.unblock": "Desbloquear",
    "action.resolve": "Resolver",
    "action.enable": "Habilitar",
    "action.disable": "Deshabilitar",

    "label.name": "Nombre",
    "label.email": "Correo electrónico",
    "label.password": "Contraseña",
    "label.description": "Descripción",
    "label.status": "Estado",
    "label.type": "Tipo",
    "label.date": "Fecha",
    "label.time": "Hora",
    "label.created": "Creado",
    "label.updated": "Actualizado",
    "label.actions": "Acciones",
    "label.all": "Todos",
    "label.none": "Ninguno",
    "label.yes": "Sí",
    "label.no": "No",
    "label.active": "Activo",
    "label.inactive": "Inactivo",
    "label.enabled": "Habilitado",
    "label.disabled": "Deshabilitado",
    "label.pending": "Pendiente",
    "label.language": "Idioma",
    "label.select_language": "Seleccionar idioma",
    "label.date_range": "Rango de fechas",
    "label.from": "Desde",
    "label.to": "Hasta",
    "label.total": "Total",
    "label.size": "Tamaño",
    "label.format": "Formato",
    "label.id": "ID",
    "label.version": "Versión",
    "label.algorithm": "Algoritmo",
    "label.key": "Clave",
    "label.value": "Valor",
    "label.ip_address": "Dirección IP",
    "label.reason": "Razón",
    "label.role": "Rol",
    "label.user": "Usuario",
    "label.tenant": "Inquilino",
    "label.organization": "Organización",
    "label.plan": "Plan",
    "label.usage": "Uso",
    "label.quota": "Cuota",
    "label.last_seen": "Última actividad",
    "label.created_at": "Creado el",
    "label.expires_at": "Expira el",
    "label.never": "Nunca",
    "label.unlimited": "Ilimitado",
    "label.online": "En línea",
    "label.offline": "Sin conexión",
    "label.unknown": "Desconocido",
    "label.loading": "Cargando...",
    "label.no_data": "No hay datos disponibles",
    "label.optional": "opcional",
    "label.required": "Obligatorio",
    "label.prefix": "Prefijo",
    "label.last_used": "Último uso",

    "login.title": "Inicia sesión en tu cuenta",
    "login.subtitle": "¡Bienvenido de nuevo! Por favor, introduce tus datos.",
    "login.email_placeholder": "Introduce tu email",
    "login.password_placeholder": "Introduce tu contraseña",
    "login.remember_me": "Recordarme",
    "login.forgot_password": "¿Olvidaste tu contraseña?",
    "login.no_account": "¿No tienes cuenta?",
    "login.sign_up": "Registrarse",
    "login.or_continue": "O continuar con",
    "login.account_locked": "Cuenta bloqueada",
    "login.too_many_attempts": "Demasiados intentos fallidos. Inténtalo de nuevo en",
    "login.seconds": "segundos",

    "register.title": "Crea tu cuenta",
    "register.subtitle": "Comienza tu viaje cuántico hoy",
    "register.first_name": "Nombre",
    "register.last_name": "Apellidos",
    "register.confirm_password": "Confirmar contraseña",
    "register.create_account": "Crear cuenta",
    "register.already_have_account": "¿Ya tienes una cuenta?",
    "register.sign_in": "Iniciar sesión",
    "register.passwords_must_match": "Las contraseñas deben coincidir",
    "register.accept_terms": "Acepto los Términos de Servicio y la Política de Privacidad",

    "2fa.title": "Autenticación de dos factores",
    "2fa.subtitle": "Introduce el código de verificación para continuar",
    "2fa.enter_code": "Introduce el código de 6 dígitos",
    "2fa.resend": "Reenviar código",
    "2fa.code_sent": "Código enviado a tu email",
    "2fa.setup_title": "Configurar autenticación de dos factores",
    "2fa.scan_qr": "Escanea este código QR con tu aplicación de autenticación",
    "2fa.backup_codes": "Códigos de respaldo",
    "2fa.download_codes": "Descargar códigos de respaldo",

    "tenant.org_name": "Nombre de la organización",
    "tenant.org_type": "Tipo de organización",
    "tenant.industry": "Sector",
    "tenant.company_size": "Tamaño de la empresa",
    "tenant.country": "País",
    "tenant.timezone": "Zona horaria",
    "tenant.primary_contact": "Contacto principal",
    "tenant.billing_email": "Email de facturación",

    "industry.technology": "Tecnología",
    "industry.finance": "Finanzas y Banca",
    "industry.healthcare": "Sanidad",
    "industry.government": "Gobierno",
    "industry.defense": "Defensa y Aeroespacial",
    "industry.telecommunications": "Telecomunicaciones",
    "industry.energy": "Energía y Servicios públicos",
    "industry.manufacturing": "Fabricación",
    "industry.retail": "Comercio minorista",
    "industry.education": "Educación",
    "industry.media": "Medios y Entretenimiento",
    "industry.gaming": "Juegos y Apuestas",
    "industry.automotive": "Automoción",
    "industry.research": "Investigación y Academia",
    "industry.other": "Otro",

    "org_type.enterprise": "Gran empresa",
    "org_type.startup": "Startup",
    "org_type.smb": "Pequeña/Mediana empresa",
    "org_type.government": "Agencia gubernamental",
    "org_type.nonprofit": "Sin ánimo de lucro",
    "org_type.educational": "Institución educativa",

    "company_size.1-10": "1-10 empleados",
    "company_size.11-50": "11-50 empleados",
    "company_size.51-200": "51-200 empleados",
    "company_size.201-500": "201-500 empleados",
    "company_size.501-1000": "501-1000 empleados",
    "company_size.1001-5000": "1001-5000 empleados",
    "company_size.5000+": "5000+ empleados",

    "device.title": "Gestión de dispositivos",
    "device.subtitle": "Registrar y monitorear dispositivos QRNG",
    "device.register": "Registrar dispositivo",
    "device.search": "Buscar dispositivos...",
    "device.device_id": "ID del dispositivo",
    "device.model": "Modelo",
    "device.firmware": "Firmware",
    "device.health": "Estado",
    "device.speed": "Velocidad",
    "device.tamper": "Manipulación",
    "device.qcnr": "QCNR",
    "device.no_devices": "No hay dispositivos registrados",
    "device.connect_first": "Registra tu primer dispositivo QRNG para empezar",
    "device.delete_confirm": "¿Eliminar este dispositivo?",
    "device.registered_at": "Registrado el",
    "device.last_heartbeat": "Último latido",
    "device.connect_info": "Conectar vía WebSocket a",
    "device.certificate_info": "Certificado mTLS requerido",

    "audit.title": "Registros de auditoría",
    "audit.subtitle": "Revisar todas las acciones administrativas y de API",
    "audit.search": "Buscar registros...",
    "audit.event_type": "Tipo de evento",
    "audit.all_events": "Todos los eventos",
    "audit.success": "Éxito",
    "audit.failed": "Fallido",
    "audit.export_csv": "Exportar CSV",
    "audit.no_logs": "No se encontraron registros de auditoría",
    "audit.user_agent": "Agente de usuario",
    "audit.endpoint": "Endpoint",
    "audit.method": "Método",
    "audit.response_code": "Código de respuesta",
    "audit.ip": "Dirección IP",
    "audit.details": "Detalles",

    "system.title": "Resumen del sistema",
    "system.subtitle": "Métricas de rendimiento y salud en tiempo real",
    "system.total_tenants": "Total inquilinos",
    "system.active_api_keys": "Claves API activas",
    "system.entropy_events": "Eventos de entropía",
    "system.system_health": "Salud del sistema",
    "system.cpu_usage": "Uso de CPU",
    "system.memory_usage": "Uso de memoria",
    "system.uptime": "Disponibilidad",
    "system.redis_status": "Estado Redis",
    "system.db_status": "Estado base de datos",
    "system.api_requests_today": "Solicitudes API hoy",
    "system.alerts_active": "Alertas activas",
    "system.connected_devices": "Dispositivos conectados",

    "alert.title": "Alertas",
    "alert.subtitle": "Monitorear alertas del sistema y notificaciones",
    "alert.no_alerts": "No hay alertas activas",
    "alert.severity": "Gravedad",
    "alert.all_severity": "Toda gravedad",
    "alert.critical": "Crítico",
    "alert.warning": "Advertencia",
    "alert.info": "Info",
    "alert.resolved": "Resuelto",
    "alert.unresolved": "No resuelto",
    "alert.mark_resolved": "Marcar como resuelto",
    "alert.timestamp": "Marca de tiempo",
    "alert.message": "Mensaje",
    "alert.source": "Fuente",

    "ip.title": "Gestión de IPs",
    "ip.subtitle": "Bloquear y gestionar direcciones IP",
    "ip.block_ip": "Bloquear IP",
    "ip.blocked_ips": "IPs bloqueadas",
    "ip.ip_address": "Dirección IP",
    "ip.blocked_at": "Bloqueado el",
    "ip.no_blocked_ips": "No hay IPs bloqueadas actualmente",
    "ip.unblock_confirm": "¿Desbloquear esta dirección IP?",
    "ip.block_reason": "Razón del bloqueo",
    "ip.permanent": "Permanente",
    "ip.temporary": "Temporal",
    "ip.expires": "Expira",

    "tenant_mgmt.title": "Gestión de inquilinos",
    "tenant_mgmt.subtitle": "Gestionar todos los inquilinos de la plataforma",
    "tenant_mgmt.create": "Incorporar inquilino",
    "tenant_mgmt.search": "Buscar inquilinos...",
    "tenant_mgmt.owner": "Propietario",
    "tenant_mgmt.api_usage": "Uso de API",
    "tenant_mgmt.no_tenants": "No se encontraron inquilinos",
    "tenant_mgmt.delete_confirm": "¿Eliminar este inquilino? Esta acción no se puede deshacer.",
    "tenant_mgmt.view_details": "Ver detalles",
    "tenant_mgmt.drbg_config": "Configuración DRBG",
    "tenant_mgmt.dedicated_pool": "Grupo dedicado",

    "customer_dash.title": "Panel de control",
    "customer_dash.welcome": "Bienvenido de nuevo",
    "customer_dash.api_calls_today": "Llamadas API hoy",
    "customer_dash.quota_used": "Cuota utilizada",
    "customer_dash.quota_remaining": "Restante",
    "customer_dash.recent_requests": "Solicitudes recientes",
    "customer_dash.quick_start": "Inicio rápido",
    "customer_dash.view_docs": "Ver documentación",
    "customer_dash.get_api_key": "Obtener clave API",
    "customer_dash.entropy_source": "Fuente de entropía",
    "customer_dash.plan_limit": "Límite del plan",
    "customer_dash.this_month": "Este mes",
    "customer_dash.uptime": "Disponibilidad",

    "api_keys.title": "Claves API",
    "api_keys.subtitle": "Gestionar tus claves API para acceder a la plataforma",
    "api_keys.create": "Crear clave API",
    "api_keys.no_keys": "Sin claves API",
    "api_keys.create_first": "Crea tu primera clave API para empezar a usar la API QRNG",
    "api_keys.key_name": "Nombre de la clave",
    "api_keys.key_prefix": "Prefijo",
    "api_keys.copy_key": "Copiar clave completa",
    "api_keys.key_created": "Clave API creada",
    "api_keys.copy_now": "Copia esta clave ahora — no se mostrará de nuevo",
    "api_keys.revoke_confirm": "¿Revocar esta clave API? Esta acción no se puede deshacer.",
    "api_keys.never_used": "Nunca utilizada",
    "api_keys.permissions": "Permisos",
    "api_keys.read_only": "Solo lectura",
    "api_keys.full_access": "Acceso completo",

    "analytics.title": "Análisis",
    "analytics.subtitle": "Estadísticas de uso de la API y tendencias",
    "analytics.requests_over_time": "Solicitudes a lo largo del tiempo",
    "analytics.top_endpoints": "Principales endpoints",
    "analytics.response_times": "Tiempos de respuesta",
    "analytics.error_rate": "Tasa de errores",
    "analytics.total_requests": "Total de solicitudes",
    "analytics.successful": "Exitosas",
    "analytics.failed": "Fallidas",
    "analytics.avg_latency": "Latencia promedio",
    "analytics.p99_latency": "Latencia P99",
    "analytics.period_day": "Hoy",
    "analytics.period_week": "Esta semana",
    "analytics.period_month": "Este mes",
    "analytics.bytes_generated": "Bytes generados",
    "analytics.no_data": "Sin datos para este período",

    "settings.title": "Configuración",
    "settings.profile": "Perfil",
    "settings.security": "Seguridad",
    "settings.notifications": "Notificaciones",
    "settings.api_settings": "Configuración API",
    "settings.change_password": "Cambiar contraseña",
    "settings.current_password": "Contraseña actual",
    "settings.new_password": "Nueva contraseña",
    "settings.confirm_new_password": "Confirmar nueva contraseña",
    "settings.save_profile": "Guardar perfil",
    "settings.first_name": "Nombre",
    "settings.last_name": "Apellidos",
    "settings.phone": "Teléfono",
    "settings.company": "Empresa",
    "settings.timezone": "Zona horaria",
    "settings.email_notifications": "Notificaciones por email",
    "settings.usage_alerts": "Alertas de uso",
    "settings.security_alerts": "Alertas de seguridad",
    "settings.marketing_emails": "Emails de marketing",
    "settings.two_factor": "Autenticación de dos factores",
    "settings.2fa_enabled": "2FA habilitado",
    "settings.2fa_disabled": "2FA deshabilitado",
    "settings.enable_2fa": "Habilitar 2FA",
    "settings.disable_2fa": "Deshabilitar 2FA",
    "settings.profile_updated": "Perfil actualizado correctamente",
    "settings.password_changed": "Contraseña cambiada correctamente",

    "pqc.title": "Criptografía post-cuántica",
    "pqc.subtitle": "Algoritmos NIST: ML-KEM-768, ML-DSA-65, SLH-DSA-SHAKE-256s",
    "pqc.kem_title": "ML-KEM-768 (FIPS 203)",
    "pqc.kem_subtitle": "Encapsulación de clave — reemplaza RSA/ECDH",
    "pqc.dsa_title": "ML-DSA-65 (FIPS 204)",
    "pqc.dsa_subtitle": "Firma digital — reemplaza ECDSA/RSA",
    "pqc.slhdsa_title": "SLH-DSA-SHAKE-256s (FIPS 205)",
    "pqc.slhdsa_subtitle": "Firma basada en hash — Nivel NIST 5",
    "pqc.vault_title": "Bóveda de claves segura",
    "pqc.vault_subtitle": "Guardar y gestionar pares de claves generados",
    "pqc.generate_keypair": "Generar par de claves",
    "pqc.save_to_vault": "Guardar en bóveda",
    "pqc.key_name_placeholder": "Nombre de la clave (opcional)",
    "pqc.no_keys_in_vault": "No hay claves guardadas",
    "pqc.delete_key_confirm": "¿Eliminar esta clave? Esta acción no se puede deshacer.",
    "pqc.key_saved": "Par de claves guardado en la bóveda",
    "pqc.key_deleted": "Clave eliminada",
    "pqc.encapsulate": "Encapsular",
    "pqc.decapsulate": "Desencapsular",
    "pqc.match_success": "Secretos compartidos coinciden — ¡intercambio de claves exitoso!",
    "pqc.match_fail": "No coinciden — intercambio de claves fallido",
    "pqc.sig_valid": "¡Firma válida — mensaje autenticado!",
    "pqc.sig_invalid": "Firma INVÁLIDA",

    "vrf.title": "Función aleatoria verificable",
    "vrf.subtitle": "ECVRF (RFC 9381 / ECVRF-P256-SHA256-TAI)",
    "vrf.generate": "Generar VRF",
    "vrf.verify": "Verificar prueba",
    "vrf.public_key": "Clave pública",
    "vrf.rotate_key": "Rotar clave",
    "vrf.input_data": "Datos de entrada",
    "vrf.output": "Salida VRF",
    "vrf.proof": "Prueba",
    "vrf.valid": "Válido",
    "vrf.invalid": "Inválido",
    "vrf.key_version": "Versión de clave",
    "vrf.rotate_confirm": "¿Rotar clave ECVRF? Las pruebas antiguas ya no verificarán.",
    "vrf.key_rotated": "Clave ECVRF rotada correctamente",

    "entropy.title": "Panel del grupo de entropía",
    "entropy.subtitle": "Grupo de entropía Redis en tiempo real — datos de dispositivos QRNG",
    "entropy.connected_devices": "Dispositivos conectados",
    "entropy.push_events": "Eventos push",
    "entropy.fill_level": "Nivel de llenado",
    "entropy.global_pool": "Grupo global",
    "entropy.no_devices": "Ningún dispositivo conectado actualmente",
    "entropy.push_history": "Actividad push reciente",
    "entropy.no_pushes": "Sin eventos push todavía",
    "entropy.redis_connected": "Redis conectado — datos QRNG en vivo",
    "entropy.redis_unavailable": "Redis no disponible",
    "entropy.auto_refresh": "Auto-actualización (5s)",
    "entropy.pool_empty": "El grupo está actualmente vacío",

    "widget.title": "Constructor de widgets",
    "widget.subtitle": "Crea y gestiona widgets del panel de control",
    "widget.create": "Crear widget",
    "widget.search": "Buscar widgets...",
    "widget.type_filter": "Filtrar por tipo",
    "widget.all_types": "Todos los tipos",
    "widget.kpi_card": "Tarjeta KPI",
    "widget.line_chart": "Gráfico de líneas",
    "widget.bar_chart": "Gráfico de barras",
    "widget.pie_chart": "Gráfico circular",
    "widget.gauge": "Indicador",
    "widget.data_table": "Tabla de datos",
    "widget.refresh_interval": "Intervalo de actualización",
    "widget.chart_type": "Tipo de gráfico",
    "widget.sql_editor": "Editor SQL",
    "widget.templates": "Plantillas",
    "widget.schema": "Esquema",
    "widget.preview": "Vista previa",
    "widget.publish": "Publicar widget",
    "widget.save_draft": "Guardar como borrador",
    "widget.alert_threshold": "Umbral de alerta",
    "widget.alert_threshold_hint": "Mostrar insignia cuando el valor ≥ este umbral",

    "role.title": "Gestión de roles",
    "role.subtitle": "Definir roles y gestionar permisos para el control de acceso",
    "role.create": "Crear rol personalizado",
    "role.search": "Buscar roles...",
    "role.system_roles": "Roles del sistema",
    "role.custom_roles": "Roles personalizados",
    "role.permissions": "Permisos",
    "role.members": "miembros",
    "role.copy_from": "Copiar permisos desde",
    "role.start_scratch": "Empezar desde cero",

    "report.title": "Generador de informes",
    "report.subtitle": "Crea y gestiona informes con gráficos y tablas",
    "report.create": "Crear informe",
    "report.search": "Buscar informes...",
    "report.chart_report": "Informe de gráficos",
    "report.tabular_report": "Informe tabular",
    "report.drilldown_report": "Informe de desglose",
    "report.schedule_report": "Programar informe",
    "report.run_now": "Ejecutar ahora",
    "report.last_run": "Última ejecución",
    "report.next_run": "Próxima ejecución",
    "report.output_format": "Formato de salida",
    "report.parameters": "Parámetros",
    "report.recipients": "Destinatarios",
    "report.frequency": "Frecuencia",
    "report.daily": "Diario",
    "report.weekly": "Semanal",
    "report.monthly": "Mensual",
    "report.custom": "Personalizado",

    "archival.title": "Gestión de archivo de datos",
    "archival.subtitle": "Gestionar políticas de retención y trabajos de archivo",
    "archival.active_data": "Datos activos",
    "archival.archived_data": "Datos archivados",
    "archival.retention_policy": "Política de retención",
    "archival.retention_years": "años de retención",
    "archival.archive_jobs": "Trabajos de archivo",
    "archival.retrieval_requests": "Solicitudes de recuperación",
    "archival.run_archive": "Ejecutar archivo",
    "archival.request_data": "Solicitar datos archivados",
    "archival.job_status": "Estado del trabajo",
    "archival.completed": "Completado",
    "archival.in_progress": "En progreso",
    "archival.failed": "Fallido",
    "archival.scheduled": "Programado",
    "archival.records_archived": "Registros archivados",
    "archival.storage_saved": "Almacenamiento ahorrado",
    "archival.data_type": "Tipo de datos",
    "archival.date_range": "Rango de fechas",
    "archival.download_link": "Enlace de descarga",
    "archival.expires_in": "Expira en",
    "archival.transactional": "Datos transaccionales",
    "archival.master": "Datos maestros",

    "billing.title": "Facturación y planes",
    "billing.subtitle": "Gestiona tu suscripción y consulta tus facturas",
    "billing.current_plan": "Plan actual",
    "billing.upgrade": "Actualizar plan",
    "billing.downgrade": "Degradar",
    "billing.payment_method": "Método de pago",
    "billing.add_payment": "Añadir método de pago",
    "billing.invoices": "Facturas",
    "billing.usage": "Uso",
    "billing.api_calls": "Llamadas API",
    "billing.bandwidth": "Ancho de banda",
    "billing.overage": "Cargos por exceso",
    "billing.no_invoices": "Sin facturas todavía",
    "billing.manage_subscription": "Gestionar suscripción",

    "status.all_systems_operational": "Todos los sistemas operativos",
    "status.loading": "Cargando...",
    "status.error": "Ocurrió un error",
    "status.success": "Éxito",
    "status.saving": "Guardando...",
    "status.saved": "Guardado",
    "status.deleting": "Eliminando...",
    "status.deleted": "Eliminado",
    "status.copied": "¡Copiado!",
    "status.not_found": "No encontrado",

    "translation.title": "Gestión de traducciones",
    "translation.subtitle": "Gestiona las traducciones para todos los idiomas",
    "translation.bulk_upload": "Carga masiva",
    "translation.export_all": "Exportar todo",
    "translation.add_key": "Añadir clave",
    "translation.key": "Clave de traducción",
    "translation.base_value": "Valor base (Inglés)",
    "translation.translation": "Traducción",
    "translation.missing": "Faltante",
    "translation.translated": "Traducido",
    "translation.upload_json": "Subir JSON",
    "translation.upload_csv": "Subir CSV",
    "translation.download_template": "Descargar plantilla",

    "validation.email_required": "El correo electrónico es obligatorio",
    "validation.password_required": "La contraseña es obligatoria",
    "validation.email_invalid": "Por favor introduce un correo electrónico válido",
    "validation.field_required": "Este campo es obligatorio",
    "validation.password_min": "La contraseña debe tener al menos 8 caracteres",
    "validation.password_uppercase": "La contraseña debe contener una mayúscula",
    "validation.password_number": "La contraseña debe contener un número",
    "validation.password_special": "La contraseña debe contener un carácter especial",
    "validation.passwords_match": "Las contraseñas deben coincidir",
    "validation.name_required": "El nombre es obligatorio",
    "validation.invalid_ip": "Por favor introduce una dirección IP válida",

    "security.title": "Seguridad",
    "security.subtitle": "Gestionar lista de bloqueo de IPs y controles de acceso",
    "security.blocked_ips": "IPs bloqueadas",
    "security.block_new_ip": "Bloquear IP",
    "security.no_blocked": "No hay IPs bloqueadas actualmente",
    "security.rate_limiting": "Limitación de velocidad",
    "security.requests_per_minute": "Solicitudes por minuto",

    "help.title": "Ayuda y soporte",
    "help.subtitle": "Documentación, tutoriales y recursos de soporte",
    "help.search": "Buscar artículos de ayuda...",
    "help.contact_support": "Contactar soporte",
    "help.open_ticket": "Abrir ticket",
    "help.faq": "Preguntas frecuentes",
    "help.documentation": "Documentación",
    "help.community": "Comunidad",
  },

  de: {
    "nav.dashboard": "Dashboard",
    "nav.api_keys": "API-Schlüssel",
    "nav.analytics": "Analytik",
    "nav.billing": "Abrechnung und Pläne",
    "nav.api_docs": "API-Dokumentation",
    "nav.settings": "Einstellungen",
    "nav.help": "Hilfe und Support",
    "nav.admin_portal": "Admin-Portal",
    "nav.customer_portal": "Kundenportal",
    "nav.system_overview": "Systemübersicht",
    "nav.device_management": "Geräteverwaltung",
    "nav.tenant_management": "Mandantenverwaltung",
    "nav.audit_logs": "Audit-Protokolle",
    "nav.alerts": "Warnungen",
    "nav.widget_builder": "Widget-Builder",
    "nav.live_dashboard": "Live-Dashboard",
    "nav.role_management": "Rollenverwaltung",
    "nav.translations": "Übersetzungen",
    "nav.report_builder": "Berichtsgenerator",
    "nav.reports": "Berichte",
    "nav.data_archival": "Datenarchivierung",
    "nav.plan_management": "Planverwaltung",
    "nav.email_config": "E-Mail-Konfiguration",
    "nav.security": "Sicherheit",
    "nav.entropy_pool": "Entropie-Pool",
    "nav.pqc": "Post-Quanten-Krypto",
    "nav.ip_management": "IP-Verwaltung",

    "action.save": "Speichern",
    "action.cancel": "Abbrechen",
    "action.delete": "Löschen",
    "action.edit": "Bearbeiten",
    "action.create": "Erstellen",
    "action.search": "Suchen",
    "action.filter": "Filtern",
    "action.export": "Exportieren",
    "action.import": "Importieren",
    "action.upload": "Hochladen",
    "action.download": "Herunterladen",
    "action.submit": "Absenden",
    "action.next": "Weiter",
    "action.previous": "Zurück",
    "action.back": "Zurück",
    "action.close": "Schließen",
    "action.confirm": "Bestätigen",
    "action.add": "Hinzufügen",
    "action.remove": "Entfernen",
    "action.view": "Anzeigen",
    "action.copy": "Kopieren",
    "action.duplicate": "Duplizieren",
    "action.refresh": "Aktualisieren",
    "action.run": "Ausführen",
    "action.schedule": "Planen",
    "action.archive": "Archivieren",
    "action.restore": "Wiederherstellen",
    "action.request": "Anfordern",
    "action.login": "Anmelden",
    "action.logout": "Abmelden",
    "action.register": "Registrieren",
    "action.revoke": "Widerrufen",
    "action.rotate": "Rotieren",
    "action.generate": "Generieren",
    "action.verify": "Verifizieren",
    "action.sign": "Signieren",
    "action.block": "Blockieren",
    "action.unblock": "Entsperren",
    "action.resolve": "Lösen",
    "action.enable": "Aktivieren",
    "action.disable": "Deaktivieren",

    "label.name": "Name",
    "label.email": "E-Mail",
    "label.password": "Passwort",
    "label.description": "Beschreibung",
    "label.status": "Status",
    "label.type": "Typ",
    "label.date": "Datum",
    "label.time": "Uhrzeit",
    "label.created": "Erstellt",
    "label.updated": "Aktualisiert",
    "label.actions": "Aktionen",
    "label.all": "Alle",
    "label.none": "Keine",
    "label.yes": "Ja",
    "label.no": "Nein",
    "label.active": "Aktiv",
    "label.inactive": "Inaktiv",
    "label.enabled": "Aktiviert",
    "label.disabled": "Deaktiviert",
    "label.pending": "Ausstehend",
    "label.language": "Sprache",
    "label.select_language": "Sprache auswählen",
    "label.date_range": "Datumsbereich",
    "label.from": "Von",
    "label.to": "Bis",
    "label.total": "Gesamt",
    "label.size": "Größe",
    "label.format": "Format",
    "label.id": "ID",
    "label.version": "Version",
    "label.algorithm": "Algorithmus",
    "label.key": "Schlüssel",
    "label.value": "Wert",
    "label.ip_address": "IP-Adresse",
    "label.reason": "Grund",
    "label.role": "Rolle",
    "label.user": "Benutzer",
    "label.tenant": "Mandant",
    "label.organization": "Organisation",
    "label.plan": "Plan",
    "label.usage": "Nutzung",
    "label.quota": "Kontingent",
    "label.last_seen": "Zuletzt gesehen",
    "label.created_at": "Erstellt am",
    "label.expires_at": "Läuft ab am",
    "label.never": "Nie",
    "label.unlimited": "Unbegrenzt",
    "label.online": "Online",
    "label.offline": "Offline",
    "label.unknown": "Unbekannt",
    "label.loading": "Wird geladen...",
    "label.no_data": "Keine Daten verfügbar",
    "label.optional": "optional",
    "label.required": "Erforderlich",
    "label.prefix": "Präfix",
    "label.last_used": "Zuletzt verwendet",

    "login.title": "Bei Ihrem Konto anmelden",
    "login.subtitle": "Willkommen zurück! Bitte geben Sie Ihre Daten ein.",
    "login.email_placeholder": "E-Mail eingeben",
    "login.password_placeholder": "Passwort eingeben",
    "login.remember_me": "Angemeldet bleiben",
    "login.forgot_password": "Passwort vergessen?",
    "login.no_account": "Kein Konto?",
    "login.sign_up": "Registrieren",
    "login.or_continue": "Oder weiter mit",
    "login.account_locked": "Konto gesperrt",
    "login.too_many_attempts": "Zu viele fehlgeschlagene Anmeldeversuche. Bitte versuchen Sie es erneut in",
    "login.seconds": "Sekunden",

    "register.title": "Konto erstellen",
    "register.subtitle": "Beginnen Sie noch heute Ihre Quantenreise",
    "register.first_name": "Vorname",
    "register.last_name": "Nachname",
    "register.confirm_password": "Passwort bestätigen",
    "register.create_account": "Konto erstellen",
    "register.already_have_account": "Sie haben bereits ein Konto?",
    "register.sign_in": "Anmelden",
    "register.passwords_must_match": "Passwörter müssen übereinstimmen",
    "register.accept_terms": "Ich akzeptiere die Nutzungsbedingungen und Datenschutzrichtlinie",

    "2fa.title": "Zwei-Faktor-Authentifizierung",
    "2fa.subtitle": "Geben Sie den Bestätigungscode ein, um fortzufahren",
    "2fa.enter_code": "6-stelligen Code eingeben",
    "2fa.resend": "Code erneut senden",
    "2fa.code_sent": "Code an Ihre E-Mail gesendet",
    "2fa.setup_title": "Zwei-Faktor-Authentifizierung einrichten",
    "2fa.scan_qr": "Scannen Sie diesen QR-Code mit Ihrer Authenticator-App",
    "2fa.backup_codes": "Sicherungscodes",
    "2fa.download_codes": "Sicherungscodes herunterladen",

    "tenant.org_name": "Organisationsname",
    "tenant.org_type": "Organisationstyp",
    "tenant.industry": "Branche",
    "tenant.company_size": "Unternehmensgröße",
    "tenant.country": "Land",
    "tenant.timezone": "Zeitzone",
    "tenant.primary_contact": "Hauptansprechpartner",
    "tenant.billing_email": "Abrechnungs-E-Mail",

    "industry.technology": "Technologie",
    "industry.finance": "Finanzen und Bankwesen",
    "industry.healthcare": "Gesundheitswesen",
    "industry.government": "Regierung",
    "industry.defense": "Verteidigung und Luft- und Raumfahrt",
    "industry.telecommunications": "Telekommunikation",
    "industry.energy": "Energie und Versorgung",
    "industry.manufacturing": "Fertigung",
    "industry.retail": "Einzel- und E-Commerce",
    "industry.education": "Bildung",
    "industry.media": "Medien und Unterhaltung",
    "industry.gaming": "Gaming und Glücksspiel",
    "industry.automotive": "Automobil",
    "industry.research": "Forschung und Wissenschaft",
    "industry.other": "Sonstiges",

    "org_type.enterprise": "Großunternehmen",
    "org_type.startup": "Startup",
    "org_type.smb": "Klein-/Mittelunternehmen",
    "org_type.government": "Regierungsbehörde",
    "org_type.nonprofit": "Gemeinnützig",
    "org_type.educational": "Bildungseinrichtung",

    "company_size.1-10": "1-10 Mitarbeiter",
    "company_size.11-50": "11-50 Mitarbeiter",
    "company_size.51-200": "51-200 Mitarbeiter",
    "company_size.201-500": "201-500 Mitarbeiter",
    "company_size.501-1000": "501-1000 Mitarbeiter",
    "company_size.1001-5000": "1001-5000 Mitarbeiter",
    "company_size.5000+": "5000+ Mitarbeiter",

    "device.title": "Geräteverwaltung",
    "device.subtitle": "QRNG-Hardware-Geräte registrieren und überwachen",
    "device.register": "Gerät registrieren",
    "device.search": "Geräte suchen...",
    "device.device_id": "Geräte-ID",
    "device.model": "Modell",
    "device.firmware": "Firmware",
    "device.health": "Gesundheit",
    "device.speed": "Geschwindigkeit",
    "device.tamper": "Manipulation",
    "device.qcnr": "QCNR",
    "device.no_devices": "Keine Geräte registriert",
    "device.connect_first": "Registrieren Sie Ihr erstes QRNG-Gerät, um zu beginnen",
    "device.delete_confirm": "Dieses Gerät löschen?",
    "device.registered_at": "Registriert am",
    "device.last_heartbeat": "Letzter Herzschlag",
    "device.connect_info": "Verbinden über WebSocket mit",
    "device.certificate_info": "mTLS-Zertifikat erforderlich",

    "audit.title": "Audit-Protokolle",
    "audit.subtitle": "Alle administrativen und API-Aktionen überprüfen",
    "audit.search": "Protokolle suchen...",
    "audit.event_type": "Ereignistyp",
    "audit.all_events": "Alle Ereignisse",
    "audit.success": "Erfolg",
    "audit.failed": "Fehlgeschlagen",
    "audit.export_csv": "CSV exportieren",
    "audit.no_logs": "Keine Audit-Protokolle gefunden",
    "audit.user_agent": "Benutzeragent",
    "audit.endpoint": "Endpunkt",
    "audit.method": "Methode",
    "audit.response_code": "Antwortcode",
    "audit.ip": "IP-Adresse",
    "audit.details": "Details",

    "system.title": "Systemübersicht",
    "system.subtitle": "Echtzeit-Gesundheits- und Leistungsmetriken der Plattform",
    "system.total_tenants": "Gesamte Mandanten",
    "system.active_api_keys": "Aktive API-Schlüssel",
    "system.entropy_events": "Entropie-Ereignisse",
    "system.system_health": "Systemgesundheit",
    "system.cpu_usage": "CPU-Auslastung",
    "system.memory_usage": "Speicherauslastung",
    "system.uptime": "Betriebszeit",
    "system.redis_status": "Redis-Status",
    "system.db_status": "Datenbankstatus",
    "system.api_requests_today": "API-Anfragen heute",
    "system.alerts_active": "Aktive Warnungen",
    "system.connected_devices": "Verbundene Geräte",

    "alert.title": "Warnungen",
    "alert.subtitle": "Systemwarnungen und Benachrichtigungen überwachen",
    "alert.no_alerts": "Keine aktiven Warnungen",
    "alert.severity": "Schweregrad",
    "alert.all_severity": "Alle Schweregrade",
    "alert.critical": "Kritisch",
    "alert.warning": "Warnung",
    "alert.info": "Info",
    "alert.resolved": "Gelöst",
    "alert.unresolved": "Ungelöst",
    "alert.mark_resolved": "Als gelöst markieren",
    "alert.timestamp": "Zeitstempel",
    "alert.message": "Nachricht",
    "alert.source": "Quelle",

    "ip.title": "IP-Verwaltung",
    "ip.subtitle": "IP-Adressen für Plattformsicherheit blockieren und verwalten",
    "ip.block_ip": "IP blockieren",
    "ip.blocked_ips": "Blockierte IPs",
    "ip.ip_address": "IP-Adresse",
    "ip.blocked_at": "Blockiert am",
    "ip.no_blocked_ips": "Derzeit keine IPs blockiert",
    "ip.unblock_confirm": "Diese IP-Adresse entsperren?",
    "ip.block_reason": "Grund für die Blockierung",
    "ip.permanent": "Dauerhaft",
    "ip.temporary": "Vorübergehend",
    "ip.expires": "Läuft ab",

    "tenant_mgmt.title": "Mandantenverwaltung",
    "tenant_mgmt.subtitle": "Alle Plattform-Mandanten verwalten",
    "tenant_mgmt.create": "Mandanten einbinden",
    "tenant_mgmt.search": "Mandanten suchen...",
    "tenant_mgmt.owner": "Eigentümer",
    "tenant_mgmt.api_usage": "API-Nutzung",
    "tenant_mgmt.no_tenants": "Keine Mandanten gefunden",
    "tenant_mgmt.delete_confirm": "Diesen Mandanten löschen? Diese Aktion kann nicht rückgängig gemacht werden.",
    "tenant_mgmt.view_details": "Details anzeigen",
    "tenant_mgmt.drbg_config": "DRBG-Konfiguration",
    "tenant_mgmt.dedicated_pool": "Dedizierter Pool",

    "customer_dash.title": "Dashboard",
    "customer_dash.welcome": "Willkommen zurück",
    "customer_dash.api_calls_today": "API-Aufrufe heute",
    "customer_dash.quota_used": "Kontingent verwendet",
    "customer_dash.quota_remaining": "Verbleibend",
    "customer_dash.recent_requests": "Letzte Anfragen",
    "customer_dash.quick_start": "Schnellstart",
    "customer_dash.view_docs": "Dokumentation anzeigen",
    "customer_dash.get_api_key": "API-Schlüssel holen",
    "customer_dash.entropy_source": "Entropiequelle",
    "customer_dash.plan_limit": "Plan-Limit",
    "customer_dash.this_month": "Diesen Monat",
    "customer_dash.uptime": "Betriebszeit",

    "api_keys.title": "API-Schlüssel",
    "api_keys.subtitle": "Ihre API-Schlüssel für den Plattformzugang verwalten",
    "api_keys.create": "API-Schlüssel erstellen",
    "api_keys.no_keys": "Noch keine API-Schlüssel",
    "api_keys.create_first": "Erstellen Sie Ihren ersten API-Schlüssel, um die QRNG-API zu nutzen",
    "api_keys.key_name": "Schlüsselname",
    "api_keys.key_prefix": "Präfix",
    "api_keys.copy_key": "Vollständigen Schlüssel kopieren",
    "api_keys.key_created": "API-Schlüssel erstellt",
    "api_keys.copy_now": "Kopieren Sie diesen Schlüssel jetzt — er wird nicht erneut angezeigt",
    "api_keys.revoke_confirm": "Diesen API-Schlüssel widerrufen? Dies kann nicht rückgängig gemacht werden.",
    "api_keys.never_used": "Nie verwendet",
    "api_keys.permissions": "Berechtigungen",
    "api_keys.read_only": "Nur lesen",
    "api_keys.full_access": "Vollzugriff",

    "analytics.title": "Analytik",
    "analytics.subtitle": "API-Nutzungsstatistiken und Trends",
    "analytics.requests_over_time": "Anfragen im Zeitverlauf",
    "analytics.top_endpoints": "Top-Endpunkte",
    "analytics.response_times": "Antwortzeiten",
    "analytics.error_rate": "Fehlerrate",
    "analytics.total_requests": "Gesamtanfragen",
    "analytics.successful": "Erfolgreich",
    "analytics.failed": "Fehlgeschlagen",
    "analytics.avg_latency": "Durchschn. Latenz",
    "analytics.p99_latency": "P99-Latenz",
    "analytics.period_day": "Heute",
    "analytics.period_week": "Diese Woche",
    "analytics.period_month": "Diesen Monat",
    "analytics.bytes_generated": "Generierte Bytes",
    "analytics.no_data": "Keine Daten für diesen Zeitraum",

    "settings.title": "Einstellungen",
    "settings.profile": "Profil",
    "settings.security": "Sicherheit",
    "settings.notifications": "Benachrichtigungen",
    "settings.api_settings": "API-Einstellungen",
    "settings.change_password": "Passwort ändern",
    "settings.current_password": "Aktuelles Passwort",
    "settings.new_password": "Neues Passwort",
    "settings.confirm_new_password": "Neues Passwort bestätigen",
    "settings.save_profile": "Profil speichern",
    "settings.first_name": "Vorname",
    "settings.last_name": "Nachname",
    "settings.phone": "Telefon",
    "settings.company": "Unternehmen",
    "settings.timezone": "Zeitzone",
    "settings.email_notifications": "E-Mail-Benachrichtigungen",
    "settings.usage_alerts": "Nutzungswarnungen",
    "settings.security_alerts": "Sicherheitswarnungen",
    "settings.marketing_emails": "Marketing-E-Mails",
    "settings.two_factor": "Zwei-Faktor-Authentifizierung",
    "settings.2fa_enabled": "2FA ist aktiviert",
    "settings.2fa_disabled": "2FA ist deaktiviert",
    "settings.enable_2fa": "2FA aktivieren",
    "settings.disable_2fa": "2FA deaktivieren",
    "settings.profile_updated": "Profil erfolgreich aktualisiert",
    "settings.password_changed": "Passwort erfolgreich geändert",

    "pqc.title": "Post-Quanten-Kryptographie",
    "pqc.subtitle": "NIST-standardisierte Algorithmen: ML-KEM-768, ML-DSA-65, SLH-DSA-SHAKE-256s",
    "pqc.kem_title": "ML-KEM-768 (FIPS 203)",
    "pqc.kem_subtitle": "Schlüsselkapselung — ersetzt RSA/ECDH",
    "pqc.dsa_title": "ML-DSA-65 (FIPS 204)",
    "pqc.dsa_subtitle": "Digitale Signatur — ersetzt ECDSA/RSA",
    "pqc.slhdsa_title": "SLH-DSA-SHAKE-256s (FIPS 205)",
    "pqc.slhdsa_subtitle": "Hash-basierte Signatur — NIST-Sicherheitsstufe 5",
    "pqc.vault_title": "Sicherer Schlüsseltresor",
    "pqc.vault_subtitle": "Generierte Schlüsselpaare speichern und verwalten",
    "pqc.generate_keypair": "Schlüsselpaar generieren",
    "pqc.save_to_vault": "In Tresor speichern",
    "pqc.key_name_placeholder": "Schlüsselname (optional)",
    "pqc.no_keys_in_vault": "Noch keine Schlüssel gespeichert",
    "pqc.delete_key_confirm": "Diesen Schlüssel löschen? Dies kann nicht rückgängig gemacht werden.",
    "pqc.key_saved": "Schlüsselpaar im Tresor gespeichert",
    "pqc.key_deleted": "Schlüssel gelöscht",
    "pqc.encapsulate": "Kapseln",
    "pqc.decapsulate": "Entkapseln",
    "pqc.match_success": "Geteilte Geheimnisse stimmen überein — Schlüsselaustausch erfolgreich!",
    "pqc.match_fail": "Nicht übereinstimmend — Schlüsselaustausch fehlgeschlagen",
    "pqc.sig_valid": "Signatur gültig — Nachricht authentifiziert!",
    "pqc.sig_invalid": "Signatur UNGÜLTIG",

    "vrf.title": "Verifizierbare Zufallsfunktion",
    "vrf.subtitle": "ECVRF (RFC 9381 / ECVRF-P256-SHA256-TAI)",
    "vrf.generate": "VRF generieren",
    "vrf.verify": "Beweis verifizieren",
    "vrf.public_key": "Öffentlicher Schlüssel",
    "vrf.rotate_key": "Schlüssel rotieren",
    "vrf.input_data": "Eingabedaten",
    "vrf.output": "VRF-Ausgabe",
    "vrf.proof": "Beweis",
    "vrf.valid": "Gültig",
    "vrf.invalid": "Ungültig",
    "vrf.key_version": "Schlüsselversion",
    "vrf.rotate_confirm": "ECVRF-Schlüssel rotieren? Alte Beweise werden nicht mehr verifiziert.",
    "vrf.key_rotated": "ECVRF-Schlüssel erfolgreich rotiert",

    "entropy.title": "Entropie-Pool-Dashboard",
    "entropy.subtitle": "Echtzeit Redis-Entropie-Pool — Live-QRNG-Gerätedaten",
    "entropy.connected_devices": "Verbundene Geräte",
    "entropy.push_events": "Push-Ereignisse",
    "entropy.fill_level": "Füllstand",
    "entropy.global_pool": "Globaler Pool",
    "entropy.no_devices": "Derzeit keine Geräte verbunden",
    "entropy.push_history": "Letzte Push-Aktivität",
    "entropy.no_pushes": "Noch keine Push-Ereignisse",
    "entropy.redis_connected": "Redis verbunden — Live-QRNG-Daten",
    "entropy.redis_unavailable": "Redis nicht verfügbar",
    "entropy.auto_refresh": "Auto-Aktualisierung (5s)",
    "entropy.pool_empty": "Pool ist derzeit leer",

    "widget.title": "Widget-Builder",
    "widget.subtitle": "Dashboard-Widgets erstellen und verwalten",
    "widget.create": "Widget erstellen",
    "widget.search": "Widgets suchen...",
    "widget.type_filter": "Nach Typ filtern",
    "widget.all_types": "Alle Typen",
    "widget.kpi_card": "KPI-Karte",
    "widget.line_chart": "Liniendiagramm",
    "widget.bar_chart": "Balkendiagramm",
    "widget.pie_chart": "Kreisdiagramm",
    "widget.gauge": "Messgerät",
    "widget.data_table": "Datentabelle",
    "widget.refresh_interval": "Aktualisierungsintervall",
    "widget.chart_type": "Diagrammtyp",
    "widget.sql_editor": "SQL-Editor",
    "widget.templates": "Vorlagen",
    "widget.schema": "Schema",
    "widget.preview": "Vorschau",
    "widget.publish": "Widget veröffentlichen",
    "widget.save_draft": "Als Entwurf speichern",
    "widget.alert_threshold": "Warnschwellenwert",
    "widget.alert_threshold_hint": "Badge anzeigen, wenn Wert ≥ Schwellenwert",

    "role.title": "Rollenverwaltung",
    "role.subtitle": "Rollen definieren und Berechtigungen für die Zugangskontrolle verwalten",
    "role.create": "Benutzerdefinierte Rolle erstellen",
    "role.search": "Rollen suchen...",
    "role.system_roles": "Systemrollen",
    "role.custom_roles": "Benutzerdefinierte Rollen",
    "role.permissions": "Berechtigungen",
    "role.members": "Mitglieder",
    "role.copy_from": "Berechtigungen kopieren von",
    "role.start_scratch": "Von Grund auf neu starten",

    "report.title": "Berichtsgenerator",
    "report.subtitle": "Berichte mit Diagrammen und Tabellen erstellen und verwalten",
    "report.create": "Bericht erstellen",
    "report.search": "Berichte suchen...",
    "report.chart_report": "Diagrammbericht",
    "report.tabular_report": "Tabellarischer Bericht",
    "report.drilldown_report": "Drill-Down-Bericht",
    "report.schedule_report": "Bericht planen",
    "report.run_now": "Jetzt ausführen",
    "report.last_run": "Letzte Ausführung",
    "report.next_run": "Nächste Ausführung",
    "report.output_format": "Ausgabeformat",
    "report.parameters": "Parameter",
    "report.recipients": "Empfänger",
    "report.frequency": "Häufigkeit",
    "report.daily": "Täglich",
    "report.weekly": "Wöchentlich",
    "report.monthly": "Monatlich",
    "report.custom": "Benutzerdefiniert",

    "archival.title": "Datenverwaltung",
    "archival.subtitle": "Aufbewahrungsrichtlinien und Archivierungsaufgaben verwalten",
    "archival.active_data": "Aktive Daten",
    "archival.archived_data": "Archivierte Daten",
    "archival.retention_policy": "Aufbewahrungsrichtlinie",
    "archival.retention_years": "Jahre Aufbewahrung",
    "archival.archive_jobs": "Archivierungsaufgaben",
    "archival.retrieval_requests": "Abrufanforderungen",
    "archival.run_archive": "Archivierungsaufgabe ausführen",
    "archival.request_data": "Archivierte Daten anfordern",
    "archival.job_status": "Aufgabenstatus",
    "archival.completed": "Abgeschlossen",
    "archival.in_progress": "In Bearbeitung",
    "archival.failed": "Fehlgeschlagen",
    "archival.scheduled": "Geplant",
    "archival.records_archived": "Archivierte Datensätze",
    "archival.storage_saved": "Gesparter Speicher",
    "archival.data_type": "Datentyp",
    "archival.date_range": "Datumsbereich",
    "archival.download_link": "Download-Link",
    "archival.expires_in": "Läuft ab in",
    "archival.transactional": "Transaktionsdaten",
    "archival.master": "Stammdaten",

    "billing.title": "Abrechnung und Pläne",
    "billing.subtitle": "Ihr Abonnement verwalten und Rechnungen einsehen",
    "billing.current_plan": "Aktueller Plan",
    "billing.upgrade": "Plan upgraden",
    "billing.downgrade": "Downgrade",
    "billing.payment_method": "Zahlungsmethode",
    "billing.add_payment": "Zahlungsmethode hinzufügen",
    "billing.invoices": "Rechnungen",
    "billing.usage": "Nutzung",
    "billing.api_calls": "API-Aufrufe",
    "billing.bandwidth": "Bandbreite",
    "billing.overage": "Überschreitungsgebühren",
    "billing.no_invoices": "Noch keine Rechnungen",
    "billing.manage_subscription": "Abonnement verwalten",

    "status.all_systems_operational": "Alle Systeme betriebsbereit",
    "status.loading": "Wird geladen...",
    "status.error": "Ein Fehler ist aufgetreten",
    "status.success": "Erfolg",
    "status.saving": "Wird gespeichert...",
    "status.saved": "Gespeichert",
    "status.deleting": "Wird gelöscht...",
    "status.deleted": "Gelöscht",
    "status.copied": "Kopiert!",
    "status.not_found": "Nicht gefunden",

    "translation.title": "Übersetzungsverwaltung",
    "translation.subtitle": "Übersetzungen für alle unterstützten Sprachen verwalten",
    "translation.bulk_upload": "Massenupload",
    "translation.export_all": "Alles exportieren",
    "translation.add_key": "Schlüssel hinzufügen",
    "translation.key": "Übersetzungsschlüssel",
    "translation.base_value": "Basiswert (Englisch)",
    "translation.translation": "Übersetzung",
    "translation.missing": "Fehlend",
    "translation.translated": "Übersetzt",
    "translation.upload_json": "JSON hochladen",
    "translation.upload_csv": "CSV hochladen",
    "translation.download_template": "Vorlage herunterladen",

    "validation.email_required": "E-Mail ist erforderlich",
    "validation.password_required": "Passwort ist erforderlich",
    "validation.email_invalid": "Bitte geben Sie eine gültige E-Mail-Adresse ein",
    "validation.field_required": "Dieses Feld ist erforderlich",
    "validation.password_min": "Passwort muss mindestens 8 Zeichen haben",
    "validation.password_uppercase": "Passwort muss einen Großbuchstaben enthalten",
    "validation.password_number": "Passwort muss eine Zahl enthalten",
    "validation.password_special": "Passwort muss ein Sonderzeichen enthalten",
    "validation.passwords_match": "Passwörter müssen übereinstimmen",
    "validation.name_required": "Name ist erforderlich",
    "validation.invalid_ip": "Bitte geben Sie eine gültige IP-Adresse ein",

    "security.title": "Sicherheit",
    "security.subtitle": "IP-Sperrliste und Zugriffskontrollen verwalten",
    "security.blocked_ips": "Blockierte IPs",
    "security.block_new_ip": "IP blockieren",
    "security.no_blocked": "Derzeit keine IPs blockiert",
    "security.rate_limiting": "Ratenbegrenzung",
    "security.requests_per_minute": "Anfragen pro Minute",

    "help.title": "Hilfe und Support",
    "help.subtitle": "Dokumentation, Tutorials und Support-Ressourcen",
    "help.search": "Hilfeartikel suchen...",
    "help.contact_support": "Support kontaktieren",
    "help.open_ticket": "Ticket öffnen",
    "help.faq": "FAQ",
    "help.documentation": "Dokumentation",
    "help.community": "Gemeinschaft",
  },
};

const SUPPORTED_LANGUAGES = [
  { code: "en", name: "English",  nativeName: "English",  flag: "US" },
  { code: "fr", name: "French",   nativeName: "Français", flag: "FR" },
  { code: "es", name: "Spanish",  nativeName: "Español",  flag: "ES" },
  { code: "de", name: "German",   nativeName: "Deutsch",  flag: "DE" },
];

const detectBrowserLanguage = () => {
  const lang = navigator.language?.split("-")[0] || "en";
  return SUPPORTED_LANGUAGES.some((l) => l.code === lang) ? lang : "en";
};

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    return localStorage.getItem("qcore_lang") || detectBrowserLanguage();
  });

  const t = useCallback(
    (key, fallback) => {
      const lang = currentLanguage;
      const translations = DEFAULT_TRANSLATIONS[lang] || DEFAULT_TRANSLATIONS.en;
      return translations[key] ?? DEFAULT_TRANSLATIONS.en[key] ?? fallback ?? key;
    },
    [currentLanguage]
  );

  const changeLanguage = useCallback((code) => {
    if (SUPPORTED_LANGUAGES.some((l) => l.code === code)) {
      setCurrentLanguage(code);
      localStorage.setItem("qcore_lang", code);
      document.documentElement.lang = code;
      document.documentElement.dir = "ltr";
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = currentLanguage;
    document.documentElement.dir = "ltr";
  }, [currentLanguage]);

  const value = {
    currentLanguage,
    changeLanguage,
    setCurrentLanguage: changeLanguage,   // alias for legacy components
    t,
    languages: SUPPORTED_LANGUAGES,
    supportedLanguages: SUPPORTED_LANGUAGES, // alias for legacy components
    detectBrowserLanguage,               // expose for LoginPage
    isRTL: false,
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) throw new Error("useLanguage must be used within a LanguageProvider");
  return context;
}

export { SUPPORTED_LANGUAGES, DEFAULT_TRANSLATIONS, detectBrowserLanguage };
